import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Row, Col, Card, Table, Badge, Offcanvas, Container, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { saveAs } from "file-saver";
import * as XLSX from 'xlsx';
import './VatReturnReport.css';
import Swal from "sweetalert2";
import TaxInvoicePreview from '../../ProformaInvoice/TaxInvoicePreview';
import { FiFileText, FiDownload, FiFilter, FiDollarSign, FiCheckCircle, FiXCircle, FiX, FiPrinter, FiMoreVertical, FiLink, FiEye, FiTrash2, FiArrowLeft, FiCalendar, FiChevronRight, FiUpload, FiRefreshCw, FiAlertCircle, FiEdit, FiPaperclip } from 'react-icons/fi';
import { FaFileExcel, FaRegFilePdf, FaPrint, FaShare } from 'react-icons/fa';
import "react-datepicker/dist/react-datepicker.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/vat-returns`;

const toNumber = (value) => {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : n;
};

const roundAmount = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

// Backend sometimes returns unclaimed_previous_count as a plain number (total)
// and sometimes as a { sales, expenses, vat_payments } breakdown object.
// This normalizes either shape into a single total so callers never silently get 0.
const getUnclaimedTotal = (count) => {
    if (count == null) return 0;
    if (typeof count === 'number') return count;
    return (count?.sales || 0) + (count?.expenses || 0) + (count?.vat_payments || 0);
};

const VatReturnReport = () => {
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showReportPage, setShowReportPage] = useState(false);
    const [vatReturns, setVatReturns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);

    // Fillable feature state
    const [showFillableModal, setShowFillableModal] = useState(false);
    const [fillableDate, setFillableDate] = useState('');
    const [selectedVatReturnForFillable, setSelectedVatReturnForFillable] = useState(null);

    // Variation Details Offcanvas State
    const [showVariationDetailsOffcanvas, setShowVariationDetailsOffcanvas] = useState(false);
    const [selectedVariationDetails, setSelectedVariationDetails] = useState(null);

    // Invoice Preview State
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [customIntegration, setCustomIntegration] = useState(false);
    const [editableDeploymentIntegration, setEditableDeploymentIntegration] = useState({});
    const [customIntegrationDescription, setCustomIntegrationDescription] = useState('');

    const [showDetailsOffcanvas, setShowDetailsOffcanvas] = useState(false);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [detailsType, setDetailsType] = useState('');
    const [taxableFilter, setTaxableFilter] = useState('taxable'); // Default to taxable
    const [detailsMonthFilter, setDetailsMonthFilter] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(null);
    const [detailsVendorFilter, setDetailsVendorFilter] = useState('');
    const [auditedItems, setAuditedItems] = useState({});
    const [auditFilter, setAuditFilter] = useState('');

    useEffect(() => {
        setDetailsMonthFilter('');
        setDetailsVendorFilter('');
    }, [selectedDetails?.type]);

    // Document Preview Offcanvas State
    const [showDocOffcanvas, setShowDocOffcanvas] = useState(false);
    const [docUrl, setDocUrl] = useState('');
    const [downloadUrl, setDownloadUrl] = useState('');
    const [docTitle, setDocTitle] = useState('');

    const [showUnclaimedOffcanvas, setShowUnclaimedOffcanvas] = useState(false);
    const [showVariationOffcanvas, setShowVariationOffcanvas] = useState(false);
    const [variationItem, setVariationItem] = useState(null);
    const [variationForm, setVariationForm] = useState({ output_amount: '', input_amount: '', notes: '' });
    const [unclaimedItems, setUnclaimedItems] = useState({
        sales_entries: [],
        expense_entries: [],
        vat_payment_entries: []
    });
    const [isFetchingLateItems, setIsFetchingLateItems] = useState(false);


    // Form state
    const [vatReturnForm, setVatReturnForm] = useState({
        period_start: '',
        period_end: '',
    });

    // Report data state
    const [vatReturnData, setVatReturnData] = useState({
        basis: 'Accrual',
        company_name: 'Systech',
        period: '',
        vat_on_sales: {
            standard_rated_supplies: [
                { emirate: 'Abu Dhabi', amount: 0, vat_amount: 0, adjustment: 0 },
                { emirate: 'Dubai', amount: 0, vat_amount: 0, adjustment: 0 },
                { emirate: 'Sharjah', amount: 0, vat_amount: 0, adjustment: 0 },
                { emirate: 'Ajman', amount: 0, vat_amount: 0, adjustment: 0 },
                { emirate: 'Umm Al Quwain', amount: 0, vat_amount: 0, adjustment: 0 },
                { emirate: 'Ras Al Khaimah', amount: 0, vat_amount: 0, adjustment: 0 },
                { emirate: 'Fujairah', amount: 0, vat_amount: 0, adjustment: 0 }
            ],
            tax_refunds_tourists: { amount: 0, vat_amount: 0, adjustment: 0 },
            reverse_charge_supplies: { amount: 0, vat_amount: 0, adjustment: 0 },
            zero_rated_supplies: { amount: 0 },
            exempt_supplies: { amount: 0 },
            goods_imported: { amount: 0, vat_amount: 0 },
            adjustments_imports: { amount: 0, vat_amount: 0 },
            totals: { taxable_amount: 0, tax_amount: 0, adjustment: 0 }
        },
        vat_on_expenses: {
            standard_rated_expenses: { amount: 0, recoverable_vat: 0, adjustment: 0 },
            reverse_charge_supplies: { amount: 0, recoverable_vat: 0, adjustment: 0 },
            totals: { taxable_amount: 0, recoverable_vat_amount: 0, adjustment: 0 }
        },
        net_vat_due: {
            total_due_tax: 0,
            total_recoverable_tax: 0,
            net_vat_payable: 0,
            request_refund: false
        },
        journal_entries: []
    });

    const [globalUnclaimed, setGlobalUnclaimed] = useState({ sales_entries: [], expense_entries: [], vat_payment_entries: [] });
    const [globalUsdExpenses, setGlobalUsdExpenses] = useState([]);
    const [usdFilterStart, setUsdFilterStart] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [usdFilterEnd, setUsdFilterEnd] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
    const [globalLoading, setGlobalLoading] = useState(false);

    useEffect(() => {
        // Set default dates
        const endDate = new Date();
        const startDate = new Date();

        setVatReturnForm({
            period_start: dayjs().format('YYYY-MM-DD'),
            period_end: dayjs().format('YYYY-MM-DD'),
        });

        // Load all required data
        fetchVatReturns();
        // fetchGlobalListData();
    }, []);

    useEffect(() => {
        if (refreshTrigger && selectedDetails) {
            if (refreshTrigger === 'emirate') {
                const emirateName = selectedDetails.title.replace(' Sales (Standard Rated)', '');
                handleEmirateClick({ emirate: emirateName });
            } else if (refreshTrigger === 'zero_rated') {
                handleZeroRatedClick();
            } else if (refreshTrigger === 'recoverable') {
                handleRecoverableClick();
            } else if (refreshTrigger === 'rcm') {
                handleRcmClick();
            }
            setRefreshTrigger(null);
        }
    }, [vatReturnData, refreshTrigger]);

    useEffect(() => {
        if (selectedReturn?.id) {
            const saved = localStorage.getItem(`vat_audit_${selectedReturn.id}`);
            if (saved) {
                try {
                    setAuditedItems(JSON.parse(saved));
                } catch (e) {
                    setAuditedItems({});
                }
            } else {
                setAuditedItems({});
            }
        }
    }, [selectedReturn?.id]);

    const toggleAudit = (entryId) => {
        setAuditedItems(prev => {
            const newState = { ...prev, [entryId]: !prev[entryId] };
            if (selectedReturn?.id) {
                localStorage.setItem(`vat_audit_${selectedReturn.id}`, JSON.stringify(newState));
            }
            return newState;
        });
    };

    const fetchGlobalListData = async () => {
        setGlobalLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // 1. Fetch Global Unclaimed (using current date as anchor)
            const unclaimedResponse = await axios.get(
                `${backendUrl}/api/vat-returns/period-transactions`,
                {
                    params: {
                        period_start: dayjs().format('YYYY-MM-DD'),
                        period_end: dayjs().format('YYYY-MM-DD'),
                        include_unclaimed: true
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true // [NEW] Custom flag to skip global loading state for this request since we also fetch USD expenses
                }
            );

            // 2. Fetch USD Expenses (using custom filter dates)
            const usdResponse = await axios.get(
                `${backendUrl}/api/vat-returns/period-transactions`,
                {
                    params: {
                        period_start: usdFilterStart,
                        period_end: usdFilterEnd,
                        include_unclaimed: false
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true // [NEW] Custom flag to skip global loading state for this request since we also fetch unclaimed items
                }
            );

            if (unclaimedResponse.data) {
                // backend returns unclaimed items in a specific structure
                // We need to look at how it's returned. Based on previous KI, it merges them.
                // But it also returns unclaimed_previous_items? Let's check.
                // Actually, I'll just use the items that came back as 'is_carried_forward'
                const allEntries = [
                    ...(unclaimedResponse.data.sales_entries || []),
                    ...(unclaimedResponse.data.expense_entries || []),
                    ...(unclaimedResponse.data.vat_payment_entries || [])
                ];

                const unclaimed = {
                    sales_entries: (unclaimedResponse.data.sales_entries || []).filter(e => e.is_carried_forward === true),
                    expense_entries: (unclaimedResponse.data.expense_entries || []).filter(e => e.is_carried_forward === true),
                    vat_payment_entries: (unclaimedResponse.data.vat_payment_entries || []).filter(e => e.is_carried_forward === true)
                };
                setGlobalUnclaimed(unclaimed);
            }

            if (usdResponse.data) {
                const usdExpenses = (usdResponse.data.expense_entries || []).filter(e =>
                    e.vendor_currency === 'USD' &&
                    !e.is_carried_forward &&
                    (e.vendor_rcm_eligible === 1 || e.vendor_rcm_eligible === true)
                );
                setGlobalUsdExpenses(usdExpenses);
            }

        } catch (error) {
            console.error('Error fetching global list data:', error);
        } finally {
            setGlobalLoading(false);
        }
    };

    const splitOrderByRevenueType = (order) => {
        const quotation = order.quotation || order;

        const totalAmountWithSubscription = parseFloat(quotation?.total_amount_with_subscription) || 0;
        const deploymentAmount = parseFloat(quotation?.deplyment_amount) || 0;

        if (totalAmountWithSubscription > 0 || deploymentAmount > 0) {
            const subscriptionItems = [];
            const deploymentItems = [];

            quotation?.product_item_details?.forEach(item => {
                const product = item.product;
                const isServiceProduct = product?.license_type?.toLowerCase() === 'service';

                if (isServiceProduct) {
                    deploymentItems.push(item);
                } else {
                    subscriptionItems.push(item);
                }
            });

            return {
                subscription: {
                    items: subscriptionItems,
                    total: totalAmountWithSubscription,
                    perMonthTotal: totalAmountWithSubscription / (quotation?.subscription_period || 1),
                    count: subscriptionItems.length
                },
                deployment: {
                    items: deploymentItems,
                    total: deploymentAmount,
                    count: deploymentItems.length
                }
            };
        }

        const subscriptionItems = [];
        const deploymentItems = [];
        let subscriptionTotal = 0;
        let deploymentTotal = 0;

        const subscriptionPeriod = quotation?.subscription_period || 1;
        const selectedLicenseType = quotation?.license_type_name?.toLowerCase();

        quotation?.product_item_details?.forEach(item => {
            const product = item.product;
            const pricings = product?.pricings || [];
            let matchingPricing = null;

            if (selectedLicenseType && pricings.length > 0) {
                matchingPricing = pricings.find(p =>
                    p.license_model?.toLowerCase() === selectedLicenseType
                );
            }

            const itemTotal = parseFloat(item.subtotal) || 0;
            const isServiceProduct = product?.license_type?.toLowerCase() === 'service';

            if (isServiceProduct) {
                deploymentItems.push(item);
                deploymentTotal += itemTotal;
            } else {
                subscriptionItems.push(item);
                subscriptionTotal += itemTotal;
            }
        });

        return {
            subscription: {
                items: subscriptionItems,
                total: subscriptionTotal,
                perMonthTotal: subscriptionTotal / subscriptionPeriod,
                count: subscriptionItems.length
            },
            deployment: {
                items: deploymentItems,
                total: deploymentTotal,
                count: deploymentItems.length
            }
        };
    };

    const handleInvoiceClick = async (entry) => {
        console.log("Invoice clicked:", entry);

        // Close details offcanvas when opening preview
        setShowDetailsOffcanvas(false);

        if (entry.tax_invoice_file) {
            setDocUrl(`${backendUrl}/storage/${entry.tax_invoice_file.replace('storage/', '')}`);
            setDownloadUrl(`${backendUrl}/api/taxinvoices/${entry.tax_invoice_id}/download`);
            setDocTitle(`Tax Invoice: ${entry.ref_no}`);
            setShowDocOffcanvas(true);
            return;
        }

        if (!entry.tax_invoice_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: 'No tax invoice associated with this transaction',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        try {
            const response = await axios.get(`${backendUrl}/api/taxinvoice/${entry.tax_invoice_id}`, {
                headers: {
                    Authorization: `Bearer ${JSON.parse(localStorage.getItem('user-info'))?.token}`,
                    'Tenant-Origin': localStorage.getItem('user-origin')
                }
            });

            console.log("Tax Invoice API Response:", response.data);
            const invoiceData = response.data.data || response.data.taxinvoice;

            if (invoiceData) {
                setSelectedInvoice(invoiceData);

                // Set quotation data and process it
                const quotation = invoiceData.proforma_invoice?.quotation || invoiceData.proformaInvoice?.quotation;
                if (quotation) {
                    const processed = {
                        ...quotation,
                        revenueSplit: splitOrderByRevenueType(quotation)
                    };
                    setSelectedQuotation(processed);

                    // Set additional required states for TaxInvoicePreview
                    setCustomIntegration(processed.custom_integration || false);
                    setCustomIntegrationDescription(processed.custom_integration_description || '');

                    try {
                        const descriptions = processed.editable_deployment_descriptions;
                        setEditableDeploymentIntegration(
                            typeof descriptions === 'string' ? JSON.parse(descriptions) : (descriptions || {})
                        );
                    } catch (e) {
                        setEditableDeploymentIntegration({});
                    }
                }

                setShowPreviewModal(true);
            } else {
                console.error("❌ Response missing taxinvoice data:", response.data);
                Swal.fire('Error', 'Could not retrieve invoice details from the server.', 'error');
            }
        } catch (error) {
            console.error("Error fetching tax invoice:", error);
            Swal.fire('Error', 'Failed to fetch tax invoice details', 'error');
        }
    };
    const handleExpenseDocClick = (entry) => {
        // [NEW] Check for both misspelled 'attatchment' and correctly spelled 'attachment'
        const attachmentPath = entry.attachment || entry.attatchment;

        if (attachmentPath) {
            // Close details offcanvas when opening preview to avoid backdrop conflicts
            setShowDetailsOffcanvas(false);

            // Standardize URL for iframe preview
            let fileUrl = attachmentPath;
            if (!fileUrl.includes('http')) {
                // Remove leading slashes
                let cleanPath = attachmentPath.startsWith('/') ? attachmentPath.substring(1) : attachmentPath;

                // Standardize path - all uploads should now be in the storage folder
                // But keep compatibility with old paths in 'uploads/'
                if (!cleanPath.startsWith('storage/')) {
                    cleanPath = `storage/${cleanPath}`;
                }

                fileUrl = `${backendUrl}/${cleanPath}`;
            }

            setDocUrl(fileUrl);

            // [NEW] Choose appropriate download API
            if (entry.is_vat_payment && entry.vat_payment_id) {
                // Use the new VAT payment download API by ID
                setDownloadUrl(`${backendUrl}/api/vat-payments/${entry.vat_payment_id}/download`);
            } else if (attachmentPath.includes('contra-statements/')) {
                // Transaction statement download API by filename
                const fileName = attachmentPath.split('/').pop();
                setDownloadUrl(`${backendUrl}/api/transactions/download/statement/${fileName}`);
            } else {
                // Standard expense download API by filename
                const fileName = attachmentPath.split('/').pop();
                setDownloadUrl(`${backendUrl}/api/expenses/download/${fileName}`);
            }

            setDocTitle(`${entry.is_vat_payment ? 'VAT Document' : 'Expense Bill'}: ${entry.invoice_no || entry.ref_no || entry.number || 'Document'}`);
            setShowDocOffcanvas(true);
        } else {
            Swal.fire('No Attachment', 'No document has been uploaded for this entry.', 'info');
        }
    };

    const fetchVatReturns = async () => {
        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${API_URL}/list`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });

            setVatReturns(response.data.data || []);
        } catch (error) {
            console.error('Error fetching VAT returns:', error);
            setVatReturns([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchVatReturnReport = async () => {
        if (!vatReturnForm.period_start || !vatReturnForm.period_end) {
            Swal.fire('Error!', 'Please select start and end dates', 'error');
            return;
        }

        setReportLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Generate the report
            const response = await axios.post(
                `${API_URL}/generate-report`,
                {
                    period_start: vatReturnForm.period_start,
                    period_end: vatReturnForm.period_end,
                    report_type: 'detailed',
                    include_unclaimed: false // [FIX] Don't auto-include - let user decide via prompt
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const reportData = response.data;

            // Transform data to match our structure for display
            const transformedData = transformReportData(reportData);
            setVatReturnData(transformedData);

            // Initial save of the report
            // [FIX] Save the ORIGINAL reportData (not transformed) to preserve all entries including carry-forward items
            const saveResponse = await axios.post(
                `${API_URL}/save`,
                {
                    period_start: vatReturnForm.period_start,
                    period_end: vatReturnForm.period_end,
                    net_vat_payable: transformedData.net_vat_due.net_vat_payable,
                    report_data: reportData, // [FIX] Save original data with all entries
                    generated_date: new Date().toISOString(),
                    submitted_at: vatReturnForm.submitted_at,

                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const savedRecordId = saveResponse.data.id;
            setSelectedReturn({
                ...saveResponse.data,
                period_start: vatReturnForm.period_start,
                period_end: vatReturnForm.period_end
            });

            // Close modal and show report page
            setShowGenerateModal(false);
            setShowReportPage(true);
            setReportLoading(false);

            // Refresh list in background
            fetchVatReturns();

            // [NEW] Check if there are unclaimed items from previous periods and prompt user
            if (reportData.has_unclaimed_from_previous) {
                const totalUnclaimed = getUnclaimedTotal(reportData.unclaimed_previous_count);

                if (totalUnclaimed > 0) {
                    const result = await Swal.fire({
                        title: 'Carry Forward VAT items',
                        html: `
                            <div style="text-align: left;">
                                <p>There are <strong>${totalUnclaimed}</strong> unclaimed VAT transactions from previous tax periods.</p>
                              
                                <p style="margin-top: 15px;">Would you like to <strong>add</strong> these items to the current VAT return, or <strong>skip</strong> them for now?</p>
                                <small class="text-muted">Skipped items will be available to claim in future returns.</small>
                            </div>
                        `,
                        icon: 'info',
                        showCancelButton: true,
                        confirmButtonText: 'Add to this Return',
                        cancelButtonText: 'Skip for Now',
                        confirmButtonColor: '#007bff',
                        cancelButtonColor: '#6c757d',
                        allowOutsideClick: false
                    });

                    // User chose to skip - just dismiss the prompt
                    // DO NOT regenerate or save - this was causing items to be marked as claimed
                    if (result.isDismissed || result.dismiss === Swal.DismissReason.cancel) {
                        Swal.fire({
                            toast: true,
                            position: 'top-end',
                            icon: 'info',
                            title: 'Unclaimed items will remain available for future returns',
                            showConfirmButton: false,
                            timer: 3000
                        });
                    } else {
                        // User chose to add - regenerate WITH unclaimed items
                        setReportLoading(true);
                        try {
                            const addResponse = await axios.post(
                                `${API_URL}/generate-report`,
                                {
                                    period_start: vatReturnForm.period_start,
                                    period_end: vatReturnForm.period_end,
                                    report_type: 'detailed',
                                    include_unclaimed: true // Include unclaimed items
                                },
                                {
                                    headers: {
                                        Authorization: `Bearer ${token}`,
                                        'Tenant-Origin': origin,
                                        'Content-Type': 'application/json'
                                    }
                                }
                            );

                            // Update with new data that includes carry-forward items
                            const newTransformedData = transformReportData(addResponse.data);
                            setVatReturnData(newTransformedData);

                            // Update the saved record
                            await axios.post(
                                `${API_URL}/save`,
                                {
                                    id: savedRecordId,
                                    period_start: vatReturnForm.period_start,
                                    period_end: vatReturnForm.period_end,
                                    net_vat_payable: newTransformedData.net_vat_due.net_vat_payable,
                                    report_data: addResponse.data,
                                    generated_date: new Date().toISOString()
                                },
                                {
                                    headers: {
                                        Authorization: `Bearer ${token}`,
                                        'Tenant-Origin': origin,
                                        'Content-Type': 'application/json'
                                    }
                                }
                            );

                            setReportLoading(false);

                            Swal.fire({
                                toast: true,
                                position: 'top-end',
                                icon: 'success',
                                title: `${totalUnclaimed} carry-forward item(s) added`,
                                showConfirmButton: false,
                                timer: 3000
                            });
                        } catch (error) {
                            setReportLoading(false);
                            console.error('Error adding unclaimed items:', error);
                            Swal.fire('Error', 'Failed to add unclaimed items', 'error');
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Error generating VAT return:', error);
            Swal.fire('Error!', error.response?.data?.error || 'Failed to generate VAT return', 'error');
        } finally {
            setReportLoading(false);
        }
    };


    const getRawSalesDisplay = (data) => {
        const rawSalesEntries = Array.isArray(data?.sales_data?.sales_entries) ? data.sales_data.sales_entries : [];
        const normalizeEmirateKey = (value) => {
            const normalized = String(value ?? '').trim().toLowerCase();
            if (normalized.includes('abu')) return 'abu_dhabi';
            if (normalized.includes('dubai')) return 'dubai';
            if (normalized.includes('sharjah')) return 'sharjah';
            if (normalized.includes('ajman')) return 'ajman';
            if (normalized.includes('umm')) return 'umm_al_quwain';
            if (normalized.includes('ras')) return 'ras_al_khaimah';
            if (normalized.includes('fujairah')) return 'fujairah';
            return 'dubai';
        };
        const grouped = {
            abu_dhabi: { emirate: 'Abu Dhabi', amount: 0, vat_amount: 0, adjustment: 0 },
            dubai: { emirate: 'Dubai', amount: 0, vat_amount: 0, adjustment: 0 },
            sharjah: { emirate: 'Sharjah', amount: 0, vat_amount: 0, adjustment: 0 },
            ajman: { emirate: 'Ajman', amount: 0, vat_amount: 0, adjustment: 0 },
            umm_al_quwain: { emirate: 'Umm Al Quwain', amount: 0, vat_amount: 0, adjustment: 0 },
            ras_al_khaimah: { emirate: 'Ras Al Khaimah', amount: 0, vat_amount: 0, adjustment: 0 },
            fujairah: { emirate: 'Fujairah', amount: 0, vat_amount: 0, adjustment: 0 }
        };

        rawSalesEntries.forEach((entry) => {
            if (entry.claimed === false) return;
            const vatAmount = toNumber(entry.vat_amount ?? entry.tax_amount ?? 0);
            const vatType = String(entry.vat_type || 'standard').toLowerCase();
            if (vatAmount <= 0 || vatType === 'reverse_charge') return;
            const key = normalizeEmirateKey(entry.emirate || entry.customer_state || entry.state);
            if (!grouped[key]) return;
            grouped[key].amount += toNumber(entry.taxable_amount ?? (toNumber(entry.amount ?? entry.total_amount ?? 0) - vatAmount));
            grouped[key].vat_amount += vatAmount;
            grouped[key].adjustment += toNumber(entry.adjustment ?? 0);
        });

        const rows = ['abu_dhabi', 'dubai', 'sharjah', 'ajman', 'umm_al_quwain', 'ras_al_khaimah', 'fujairah'].map((key) => ({
            ...grouped[key],
            amount: roundAmount(grouped[key].amount),
            vat_amount: roundAmount(grouped[key].vat_amount),
            adjustment: roundAmount(grouped[key].adjustment)
        }));

        const totals = rows.reduce((acc, row) => ({
            taxable_amount: roundAmount(acc.taxable_amount + row.amount),
            tax_amount: roundAmount(acc.tax_amount + row.vat_amount),
            adjustment: roundAmount(acc.adjustment + row.adjustment)
        }), { taxable_amount: 0, tax_amount: 0, adjustment: 0 });

        return { rows, totals };
    };

    const getRawExpenseDisplay = (data) => {
        const rawExpenseEntries = Array.isArray(data?.expenses_data?.expense_entries) ? data.expenses_data.expense_entries : [];
        const standardEntries = rawExpenseEntries.filter((entry) => {
            if (entry.claimed === false) return false;
            const vatAmount = toNumber(entry.vat_amount ?? entry.tax_amount ?? 0);
            const vatType = String(entry.vat_type || 'standard').toLowerCase();
            return vatAmount > 0 && vatType !== 'reverse_charge';
        });
        const reverseEntries = rawExpenseEntries.filter((entry) => {
            if (entry.claimed === false) return false;
            const vatAmount = toNumber(entry.vat_amount ?? entry.tax_amount ?? 0);
            const vatType = String(entry.vat_type || '').toLowerCase();
            return vatAmount > 0 && vatType === 'reverse_charge';
        });

const standardAmount = standardEntries.reduce((sum, entry) => sum + toNumber(entry.taxable_amount ?? 0), 0);
        const standardVat = standardEntries.reduce((sum, entry) => sum + toNumber(entry.vat_amount ?? entry.tax_amount ?? 0), 0);
        const standardAdjustment = standardEntries.reduce((sum, entry) => sum + toNumber(entry.adjustment ?? 0), 0);

const reverseAmount = reverseEntries.reduce((sum, entry) => sum + toNumber(entry.taxable_amount ?? 0), 0);
        const reverseVat = reverseEntries.reduce((sum, entry) => sum + toNumber(entry.vat_amount ?? entry.tax_amount ?? 0), 0);
        const reverseAdjustment = reverseEntries.reduce((sum, entry) => sum + toNumber(entry.adjustment ?? 0), 0);

        return {
            standard_rated_expenses: {
                amount: roundAmount(standardAmount),
                recoverable_vat: roundAmount(standardVat),
                adjustment: roundAmount(standardAdjustment)
            },
            reverse_charge_supplies: {
                amount: roundAmount(reverseAmount),
                recoverable_vat: roundAmount(reverseVat),
                adjustment: roundAmount(reverseAdjustment)
            },
            totals: {
                taxable_amount: roundAmount(standardAmount + reverseAmount),
                recoverable_vat_amount: roundAmount(standardVat + reverseVat),
                adjustment: roundAmount(standardAdjustment + reverseAdjustment)
            }
        };
    };

    const transformReportData = (data) => {
        // Transform the API data to match Zoho format
        const periodStart = data.period_start || data.summary?.period?.start_date;
        const periodEnd = data.period_end || data.summary?.period?.end_date;

        const salesDisplay = getRawSalesDisplay(data);
        const expenseDisplay = getRawExpenseDisplay(data);

        const rawSalesEntries = Array.isArray(data?.sales_data?.sales_entries) ? data.sales_data.sales_entries : [];
        const rawExpenseEntries = Array.isArray(data?.expenses_data?.expense_entries) ? data.expenses_data.expense_entries : [];

        const totalDueTax = rawSalesEntries.length > 0
            ? salesDisplay.totals.tax_amount
            : roundAmount(toNumber(data.net_vat_due?.total_due_tax ?? data.vat_on_sales?.totals?.tax_amount ?? 0));

        const totalRecoverableTax = rawExpenseEntries.length > 0
            ? expenseDisplay.totals.recoverable_vat_amount
            : roundAmount(toNumber(data.net_vat_due?.total_recoverable_tax ?? data.vat_on_expenses?.totals?.recoverable_vat_amount ?? 0));

        const netPayable = roundAmount(totalDueTax - totalRecoverableTax);

        const transformed = {
            basis: 'Accrual',
            company_name: 'Systech',
            // period: `${formatDateDisplay(data.summary?.period?.start_date || data.period_start)} To ${formatDateDisplay(data.summary?.period?.end_date || data.period_end)}`,
            period: periodStart && periodEnd
                ? `${formatDateDisplay(periodStart)} To ${formatDateDisplay(periodEnd)}`
                : (data.period || ''),
            vat_on_sales: {
                standard_rated_supplies: salesDisplay.rows,
                tax_refunds_tourists: {
                    amount: typeof data.vat_on_sales?.tax_refunds_tourists === 'object'
                        ? (data.vat_on_sales.tax_refunds_tourists.amount || 0)
                        : (data.vat_on_sales?.tax_refunds_tourists || 0),
                    vat_amount: typeof data.vat_on_sales?.tax_refunds_tourists === 'object'
                        ? (data.vat_on_sales.tax_refunds_tourists.vat_amount || 0)
                        : 0,
                    adjustment: typeof data.vat_on_sales?.tax_refunds_tourists === 'object'
                        ? (data.vat_on_sales.tax_refunds_tourists.adjustment || 0)
                        : 0
                },
                reverse_charge_supplies: {
                    amount: typeof data.vat_on_sales?.reverse_charge_supplies === 'object'
                        ? (data.vat_on_sales.reverse_charge_supplies.amount || 0)
                        : (data.vat_on_sales?.reverse_charge_supplies || 0),
                    vat_amount: typeof data.vat_on_sales?.reverse_charge_supplies === 'object'
                        ? (data.vat_on_sales.reverse_charge_supplies.vat_amount || 0)
                        : 0,
                    adjustment: typeof data.vat_on_sales?.reverse_charge_supplies === 'object'
                        ? (data.vat_on_sales.reverse_charge_supplies.adjustment || 0)
                        : 0
                },
                zero_rated_supplies: {
                    amount: typeof data.vat_on_sales?.zero_rated_supplies === 'object'
                        ? (data.vat_on_sales.zero_rated_supplies.amount || 0)
                        : (data.vat_on_sales?.zero_rated_supplies || 0),
                    vat_amount: 0,
                    adjustment: 0
                },
                exempt_supplies: {
                    amount: typeof data.vat_on_sales?.exempt_supplies === 'object'
                        ? (data.vat_on_sales.exempt_supplies.amount || 0)
                        : (data.vat_on_sales?.exempt_supplies || 0),
                    vat_amount: 0,
                    adjustment: 0
                },
                goods_imported: {
                    amount: typeof data.vat_on_sales?.goods_imported === 'object'
                        ? (data.vat_on_sales.goods_imported.amount || 0)
                        : (data.vat_on_sales?.goods_imported || 0),
                    vat_amount: typeof data.vat_on_sales?.goods_imported === 'object'
                        ? (data.vat_on_sales.goods_imported.vat_amount || 0)
                        : 0,
                    adjustment: typeof data.vat_on_sales?.goods_imported === 'object'
                        ? (data.vat_on_sales.goods_imported.adjustment || 0)
                        : 0
                },
                adjustments_imports: {
                    amount: typeof data.vat_on_sales?.adjustments_imports === 'object'
                        ? (data.vat_on_sales.adjustments_imports.amount || 0)
                        : (data.vat_on_sales?.adjustments_imports || 0),
                    vat_amount: typeof data.vat_on_sales?.adjustments_imports === 'object'
                        ? (data.vat_on_sales.adjustments_imports.vat_amount || 0)
                        : 0,
                    adjustment: typeof data.vat_on_sales?.adjustments_imports === 'object'
                        ? (data.vat_on_sales.adjustments_imports.adjustment || 0)
                        : 0
                },
                totals: {
                    taxable_amount: rawSalesEntries.length > 0 ? salesDisplay.totals.taxable_amount : (data.vat_on_sales?.totals?.taxable_amount || 0),
                    tax_amount: totalDueTax,
                    adjustment: rawSalesEntries.length > 0 ? salesDisplay.totals.adjustment : (data.vat_on_sales?.totals?.adjustment || 0)
                }
            },
            vat_on_expenses: {
                standard_rated_expenses: expenseDisplay.standard_rated_expenses,
                reverse_charge_supplies: expenseDisplay.reverse_charge_supplies,
                totals: expenseDisplay.totals
            },
            net_vat_due: {
                total_due_tax: totalDueTax,
                total_recoverable_tax: totalRecoverableTax,
                net_vat_payable: netPayable,
                request_refund: netPayable < 0
            },
            journal_entries: generateJournalEntries(totalDueTax, totalRecoverableTax),
            // [FIX] Preserve raw data for detailed view and claim toggling persistence
            sales_data: data.sales_data,
            expenses_data: data.expenses_data,
            vat_ledgers: data.vat_ledgers,
            has_unclaimed_from_previous: data.has_unclaimed_from_previous,
            unclaimed_previous_count: data.unclaimed_previous_count,
            unclaimed_previous_items: data.unclaimed_previous_items, // [NEW] Store the list of items
            submitted_at: data.submitted_at
        };

        return transformed;
    };

    const generateJournalEntries = (dueTax, recoverableTax) => {
        const entries = [];
        const due = typeof dueTax === 'number' ? dueTax : (dueTax?.vat_on_sales?.totals?.tax_amount || 0);
        const recoverable = typeof recoverableTax === 'number' ? recoverableTax : (dueTax?.vat_on_expenses?.totals?.tax_amount || 0);
        if (due > 0) {
            entries.push({
                debit_account: 'Output VAT',
                credit_account: 'VAT Payable',
                amount: due,
                description: 'VAT on sales for the period'
            });
        }
        if (recoverable > 0) {
            entries.push({
                debit_account: 'VAT Payable',
                credit_account: 'Input VAT',
                amount: recoverable,
                description: 'VAT on expenses for the period'
            });
        }
        return entries;
    };

    const viewVatReturn = async (vatReturn) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(
                `${API_URL}/${vatReturn.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                }
            );

            const serverReportData = response.data.report_data || response.data.journal_data || response.data;
            const transformedData = transformReportData(serverReportData);
            const periodStart = response.data.period_start || vatReturn.period_start;
            const periodEnd = response.data.period_end || vatReturn.period_end;

            // Update vatReturnForm with the period dates
            setVatReturnForm({
                period_start: periodStart,
                period_end: periodEnd,
            });
            setVatReturnData(transformedData);
            setSelectedReturn(response.data);
            setShowReportPage(true);

            // Carry Forward Alert - Show ONLY if return is unfiled (payment_status is 'generated')
            // If payment_status is 'submitted' or has submitted_at date, it is FILED, so alert is suppressed.
            const isFiled = (response.data.payment_status === 'submitted' || Boolean(response.data.submitted_at)) &&
                            response.data.payment_status !== 'generated';

            const hasUnclaimed = Boolean(serverReportData.has_unclaimed_from_previous);
            const isIncluded = Boolean(serverReportData.include_unclaimed);
            const totalUnclaimed = getUnclaimedTotal(serverReportData.unclaimed_previous_count);

            if (hasUnclaimed && !isIncluded && !isFiled && totalUnclaimed > 0) {

                if (totalUnclaimed > 0) {
                    setTimeout(async () => {
                        const result = await Swal.fire({
                            title: 'Carry Forward VAT items',
                            html: `
                                <div style="text-align: left;">
                                    <p>There are <strong>${totalUnclaimed}</strong> unclaimed VAT transactions from previous tax periods that were skipped.</p>
                                    <p style="margin-top: 15px;">Would you like to <strong>add</strong> these items to this VAT return now?</p>
                                    <small class="text-muted">Adding them will update the totals for this return.</small>
                                </div>
                            `,
                            icon: 'info',
                            showCancelButton: true,
                            confirmButtonText: 'Yes, Add them',
                            cancelButtonText: 'Keep Skipped',
                            confirmButtonColor: '#007bff',
                            cancelButtonColor: '#6c757d',
                        });

                        if (result.isConfirmed) {
                            setReportLoading(true);
                            try {
                                // Regenerate WITH unclaimed items
                                const addResponse = await axios.post(
                                    `${API_URL}/generate-report`,
                                    {
                                        period_start: periodStart,
                                        period_end: periodEnd,
                                        report_type: 'detailed',
                                        include_unclaimed: true
                                    },
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            'Tenant-Origin': origin,
                                            'Content-Type': 'application/json'
                                        }
                                    }
                                );

                                const finalData = transformReportData(addResponse.data);
                                setVatReturnData(finalData);

                                // Save/Update the record
                                const saveResponse = await axios.post(
                                    `${API_URL}/save`,
                                    {
                                        id: vatReturn.id,
                                        period_start: periodStart,
                                        period_end: periodEnd,
                                        net_vat_payable: finalData.net_vat_due.net_vat_payable,
                                        report_data: addResponse.data,
                                        generated_date: new Date().toISOString()
                                    },
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            'Tenant-Origin': origin,
                                            'Content-Type': 'application/json'
                                        }
                                    }
                                );

                                setSelectedReturn(saveResponse.data.data);

                                Swal.fire({
                                    toast: true,
                                    position: 'top-end',
                                    icon: 'success',
                                    title: 'Unclaimed items added and return updated',
                                    showConfirmButton: false,
                                    timer: 3000
                                });
                            } catch (err) {
                                console.error('Error adding unclaimed items:', err);
                                Swal.fire('Error', 'Failed to update VAT return', 'error');
                            } finally {
                                setReportLoading(false);
                            }
                        }
                    }, 500);
                }
            }

        } catch (error) {
            console.error('Error viewing VAT return:', error);
            Swal.fire('Error!', 'Failed to load VAT return details', 'error');
        }
    };

    const handleOpenVariation = async (item) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(
                `${API_URL}/${item.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                }
            );

            const fullData = response.data;
            const reportData = typeof fullData.report_data === 'string' ? JSON.parse(fullData.report_data) : (fullData.report_data || fullData.journal_data || fullData);

            const totalOutputVat = reportData?.net_vat_due?.total_due_tax || 0;
            const totalInputVat = reportData?.net_vat_due?.total_recoverable_tax || 0;

            setVariationItem({
                ...item,
                totalOutputVat,
                totalInputVat
            });

            setVariationForm({
                output_amount: (item.output_variation_amount !== null && item.output_variation_amount !== undefined && item.output_variation_amount !== '') ? (parseFloat(totalOutputVat) + parseFloat(item.output_variation_amount)).toFixed(2) : '',
                input_amount: (item.input_variation_amount !== null && item.input_variation_amount !== undefined && item.input_variation_amount !== '') ? (parseFloat(totalInputVat) + parseFloat(item.input_variation_amount)).toFixed(2) : '',
                notes: item.variation_notes || ''
            });

            setShowVariationOffcanvas(true);
        } catch (error) {
            console.error("Error fetching VAT details for variation:", error);
            Swal.fire("Error", "Failed to fetch VAT details", "error");
        }
    };

    const handleSaveVariation = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const diffOutput = variationForm.output_amount !== '' && variationForm.output_amount !== null ? (parseFloat(variationForm.output_amount) - parseFloat(variationItem.totalOutputVat || 0)).toFixed(2) : null;
            const diffInput = variationForm.input_amount !== '' && variationForm.input_amount !== null ? (parseFloat(variationForm.input_amount) - parseFloat(variationItem.totalInputVat || 0)).toFixed(2) : null;

            await axios.post(
                `${API_URL}/${variationItem.id}/variation`,
                {
                    output_variation_amount: diffOutput,
                    input_variation_amount: diffInput,
                    variation_notes: variationForm.notes
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                }
            );

            Swal.fire('Success', 'Variation updated successfully', 'success');
            setShowVariationOffcanvas(false);
            fetchVatReturns();
        } catch (error) {
            console.error("Error saving variation:", error);
            Swal.fire('Error', 'Failed to update variation', 'error');
        }
    };

    const deleteVatReturn = async (vatReturn) => {
        if (vatReturn.is_paid == 1 || vatReturn.is_paid === true) {
            Swal.fire({
                title: 'Warning!',
                text: 'Is it already payed it cant delete first delete the trasanction then delete this.',
                icon: 'warning'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Delete VAT Return?',
            html: `Are you sure you want to delete the VAT return for period <br/><strong>${formatDate(vatReturn.period_start)} to ${formatDate(vatReturn.period_end)}</strong>?<br/><br/>This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                await axios.delete(
                    `${API_URL}/${vatReturn.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        }
                    }
                );

                Swal.fire({
                    title: 'Deleted!',
                    text: 'VAT return has been deleted successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                fetchVatReturns();

            } catch (error) {
                console.error('Error deleting VAT return:', error);
                Swal.fire(
                    'Error!',
                    error.response?.data?.message || 'Failed to delete VAT return. Please try again.',
                    'error'
                );
            }
        }
    };

    const openFillableModal = (vatReturn) => {
        setSelectedVatReturnForFillable(vatReturn);
        setFillableDate(new Date().toISOString().split('T')[0]); // Default to today
        setShowFillableModal(true);
    };

    const handleSaveFillableDate = async () => {
        if (!fillableDate) {
            Swal.fire('Error!', 'Please select a fillable date', 'error');
            return;
        }

        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Get the full VAT return data
            const vatReturnResponse = await axios.get(
                `${API_URL}/${selectedVatReturnForFillable.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                }
            );

            const vatReturnData = vatReturnResponse.data;

            // Call the mark-as-filed endpoint so the backend actually creates the journal entry
            // NOTE: this lives under the vat-payments controller, not vat-returns
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            await axios.post(
                `${backendUrl}/api/vat-payments/${selectedVatReturnForFillable.id}/mark-as-filed`,
                {
                    submitted_at: fillableDate
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true // [FIX] Skip global loader to prevent UI reset
                }
            );

            Swal.fire({
                title: 'Success!',
                text: 'VAT return marked as fillable successfully.',
                icon: 'success',
                timer: 3000,
                showConfirmButton: false
            });

            // Close modal and refresh list
            setShowFillableModal(false);
            setSelectedVatReturnForFillable(null);
            setFillableDate('');
            fetchVatReturns();

        } catch (error) {
            console.error('Error saving fillable date:', error);
            Swal.fire(
                'Error!',
                error.response?.data?.message || 'Failed to save fillable date. Please try again.',
                'error'
            );
        }
    };

    const handleVatReturnFormChange = (field, value) => {
        // Handle DatePicker (dayjs object) or regular input (event object)
        if (value && value.target) {
            // Regular input event
            const { name, value: inputValue } = value.target;
            setVatReturnForm((prev) => ({
                ...prev,
                [name]: inputValue
            }));
        } else {
            // DatePicker value (dayjs object)
            setVatReturnForm((prev) => ({
                ...prev,
                [field]: value ? value.format('YYYY-MM-DD') : ''
            }));
        }
    };

    const handleOpenGenerateModal = () => {
        if (vatReturns && vatReturns.length > 0) {
            // Find the most recent return by period_end
            // We sort by period_end descending to get the absolute latest date
            const sortedReturns = [...vatReturns].sort((a, b) => dayjs(b.period_end).diff(dayjs(a.period_end)));
            const latestReturn = sortedReturns[0];

            const nextStart = dayjs(latestReturn.period_end).add(1, 'day');

            // Find typical quarter end. If nextStart is Jan-Mar -> end Mar, Apr-Jun -> end Jun, etc.
            // Or just add 3 months and end of month
            const nextEnd = nextStart.add(2, 'month').endOf('month');

            setVatReturnForm({
                period_start: nextStart.format('YYYY-MM-DD'),
                period_end: nextEnd.format('YYYY-MM-DD'),
            });
        } else {
            // Fallback to current quarter if no returns exist
            const now = dayjs();
            const nextStart = now.startOf('quarter');
            const nextEnd = now.endOf('quarter');

            setVatReturnForm({
                period_start: nextStart.format('YYYY-MM-DD'),
                period_end: nextEnd.format('YYYY-MM-DD'),
            });
        }
        setShowGenerateModal(true);
    };
    // Add a new state for details loading
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Update both API calls to use the correct endpoint
    const handleEmirateClick = async (emirateData) => {
        try {
            // Resolve dates: use selectedReturn if available, otherwise fallback to form
            const startDate = selectedReturn?.period_start || vatReturnForm.period_start;
            const endDate = selectedReturn?.period_end || vatReturnForm.period_end;
            if (!startDate || !endDate) {
                console.error("Missing dates for offcanvas", { selectedReturn, vatReturnForm });
                return;
            }

            // Mapping Display Name to snake_case key used in backend
            const emirateKeyMap = {
                'Abu Dhabi': 'abu_dhabi',
                'Dubai': 'dubai',
                'Sharjah': 'sharjah',
                'Ajman': 'ajman',
                'Umm Al Quwain': 'umm_al_quwain',
                'Ras Al Khaimah': 'ras_al_khaimah',
                'Fujairah': 'fujairah'
            };

            const targetKey = emirateKeyMap[emirateData.emirate] || 'dubai';

            // Filter by BOTH emirate and standard vat type
            const emirateEntries = (vatReturnData.sales_data?.sales_entries || []).filter(e =>
                (e.emirate === targetKey || (targetKey === 'dubai' && !e.emirate)) &&
                e.vat_type === 'standard'
            );

            // [NEW] Check if there are carry-forward items and prompt user
            const carryForwardItems = emirateEntries.filter(e => e.is_carried_forward);

            console.log("🔍 Carry Forward Detection (Emirate):", {
                emirate: emirateData.emirate,
                totalCount: carryForwardItems.length,
                hasUnclaimedFromPrevious: vatReturnData.has_unclaimed_from_previous
            });

            // [FIX] Simplify Logic: Remove Alert and just show the list
            // 1. Show all current period items
            // 2. Show Carry-Forward items ONLY if they are explicitly marked as 'claimed: true'
            // 3. Strict Check: If item is OLD but missing carry_forward flag (legacy bug), Hide it

            const finalEntries = emirateEntries.filter(e => {
                const startDateObj = new Date(startDate);
                const itemDateObj = new Date(e.date);
                const isOld = itemDateObj < startDateObj;

                if (isOld && !e.is_carried_forward) {
                    return false; // Hide legacy/bugged items
                }

                if (e.is_carried_forward) {
                    return e.claimed === true;
                }
                return true;
            });

            setSelectedDetails({
                type: 'emirate',
                title: `${emirateData.emirate} Sales (Standard Rated)`,
                data: finalEntries,
                period_start: startDate,
                period_end: endDate,
                totalAmount: finalEntries.reduce((sum, entry) => sum + (entry.claimed !== false ? (parseFloat(entry.amount) || 0) : 0), 0),
                vatAmount: finalEntries.reduce((sum, entry) => sum + (entry.claimed !== false ? (parseFloat(entry.vat_amount) || 0) : 0), 0)
            });

            setDetailsType('emirate');
            setShowDetailsOffcanvas(true);

        } catch (error) {
            console.error('Error fetching period transactions:', error);
            Swal.fire('Error!', 'Failed to load transaction details', 'error');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleZeroRatedClick = async () => {
        try {
            const startDate = selectedReturn?.period_start || vatReturnForm.period_start;
            const endDate = selectedReturn?.period_end || vatReturnForm.period_end;

            if (!startDate || !endDate) return;

            const zeroRatedEntries = (vatReturnData.sales_data?.sales_entries || []).filter(e =>
                e.vat_type === 'zero_rated' || e.vat_type === 'zero'
            );

            // [NEW] Check if there are carry-forward items and prompt user
            const carryForwardItems = zeroRatedEntries.filter(e => e.is_carried_forward);

            console.log("🔍 Carry Forward Detection (Zero Rated):", {
                totalCount: carryForwardItems.length,
                hasUnclaimedFromPrevious: vatReturnData.has_unclaimed_from_previous
            });

            // [FIX] Simplify Logic: Remove Alert and just show the list
            // 1. Show all current period items
            // 2. Show Carry-Forward items ONLY if they are explicitly marked as 'claimed: true'
            // 3. Strict Check: If item is OLD but missing carry_forward flag (legacy bug), Hide it

            const finalEntries = zeroRatedEntries.filter(e => {
                const startDateObj = new Date(startDate);
                const itemDateObj = new Date(e.date);
                const isOld = itemDateObj < startDateObj;

                if (isOld && !e.is_carried_forward) {
                    return false; // Hide legacy/bugged items
                }

                if (e.is_carried_forward) {
                    return e.claimed === true;
                }
                return true;
            });

            setSelectedDetails({
                type: 'zero_rated',
                title: 'Zero Rated Supplies (Box 4)',
                data: finalEntries,
                period_start: startDate,
                period_end: endDate,
                totalAmount: finalEntries.reduce((sum, entry) => sum + (entry.claimed !== false ? (entry.amount || 0) : 0), 0),
                vatAmount: 0
            });

            setDetailsType('zero_rated');
            setShowDetailsOffcanvas(true);

        } catch (error) {
            console.error('Error fetching zero rated transactions:', error);
            Swal.fire('Error!', 'Failed to load zero-rated details', 'error');
        } finally {
            setDetailsLoading(false);
        }
    };

    // Update handleRecoverableClick to use correct URL
    // const handleRecoverableClick = async () => {
    //     try {
    //         // Resolve dates: use selectedReturn if available, otherwise fallback to form
    //         const startDate = selectedReturn?.period_start || vatReturnForm.period_start;
    //         const endDate = selectedReturn?.period_end || vatReturnForm.period_end;

    //         if (!startDate || !endDate) {
    //             console.error("Missing dates for offcanvas", { selectedReturn, vatReturnForm });
    //             return;
    //         }

    //         setDetailsLoading(true);

    //         const userInfo = JSON.parse(localStorage.getItem('user-info'));
    //         const origin = localStorage.getItem('user-origin');
    //         const token = userInfo?.token;

    //         // Fix: Use the correct endpoint URL
    //         const response = await axios.get(
    //             `${backendUrl}/api/vat-returns/period-transactions`, // Fixed URL
    //             {
    //                 params: {
    //                     period_start: startDate,
    //                     period_end: endDate
    //                 },
    //                 headers: {
    //                     Authorization: `Bearer ${token}`,
    //                     'Tenant-Origin': origin,
    //                 },
    //             }
    //         );

    //         setSelectedDetails({
    //             type: 'recoverable',
    //             title: 'Recoverable VAT - Expenses',
    //             data: response.data.expense_entries || [],
    //             period_start: startDate,
    //             period_end: endDate,
    //             totalAmount: response.data.expense_entries.reduce((sum, entry) => sum + (entry.amount || 0), 0),
    //             vatAmount: response.data.expense_entries.reduce((sum, entry) => sum + (entry.vat_amount || 0), 0)
    //         });
    //         setDetailsType('recoverable');
    //         setShowDetailsOffcanvas(true);

    //     } catch (error) {
    //         console.error('Error fetching period transactions:', error);
    //         Swal.fire('Error!', 'Failed to load expense details', 'error');
    //     } finally {
    //         setDetailsLoading(false);
    //     }
    // };

    const handleRcmClick = async () => {
        try {
            const startDate = selectedReturn?.period_start || vatReturnForm.period_start;
            const endDate = selectedReturn?.period_end || vatReturnForm.period_end;

            if (!startDate || !endDate) return;

            // Filter for RCM entries from both sales (Box 3) and expenses (Box 10)
            const rcmSales = (vatReturnData.sales_data?.sales_entries || []).filter(e =>
                (e.vat_type === 'reverse_charge' ||
                    (e.narration || '').toLowerCase().includes('rcm') ||
                    (e.narration || '').toLowerCase().includes('reverse charge')) &&
                !e.is_carried_forward
            ).map(e => ({ ...e, detailsType: 'emirate' })); // Use emirate style for sales click

            const rcmExpenses = (vatReturnData.expenses_data?.expense_entries || []).filter(entry => {
                const entryNarration = (entry.narration || '').toLowerCase();
                const isRCM =
                    entry.rcm_eligible === 1 || entry.rcm_eligible === true ||
                    entry.vendor_rcm_eligible === 1 || entry.vendor_rcm_eligible === true ||
                    entryNarration.includes('rcm')
                return isRCM && !entry.is_carried_forward;
            });

            const combinedEntries = [...rcmSales, ...rcmExpenses];

            setTaxableFilter('rcm');
            setSelectedDetails({
                type: 'rcm',
                title: 'Supplies subject to Reverse Charge Provisions',
                data: combinedEntries,
                period_start: startDate,
                period_end: endDate,
            });
            setDetailsType('rcm');
            setShowDetailsOffcanvas(true);

        } catch (error) {
            console.error('Error fetching RCM details:', error);
            Swal.fire('Error!', 'Failed to load RCM details', 'error');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleRecoverableClick = async () => {
        try {
            // Resolve dates: use selectedReturn if available, otherwise fallback to form
            const startDate = selectedReturn?.period_start || vatReturnForm.period_start;
            const endDate = selectedReturn?.period_end || vatReturnForm.period_end;

            if (!startDate || !endDate) {
                console.error("Missing dates for offcanvas", { selectedReturn, vatReturnForm });
                return;
            }

            const allExpenseEntries = (vatReturnData.expenses_data?.expense_entries || []).filter(entry => {
                const hasVat = (entry.vat_amount || 0) > 0;
                const isStandard = entry.vat_type === 'standard';

                // RCM Check: check vat_type, rcm_eligible or narration
                const entryNarration = (entry.narration || '').toLowerCase();
                const isRcm = entry.vat_type === 'reverse_charge' ||
                    entry.rcm_eligible === 1 || entry.rcm_eligible === true ||
                    entry.vendor_rcm_eligible === 1 || entry.vendor_rcm_eligible === true ||
                    entryNarration.includes('rcm') || entryNarration.includes('reverse charge');

                // Petty Cash Check: check vendor name, ledger name, or narration
                const vendorName = (entry.vendor_name || '').toLowerCase();
                const ledgerName = (entry.ledger_name || '').toLowerCase();
                const isPettyCash = vendorName.includes('petty cash') ||
                    ledgerName.includes('petty cash') ||
                    entryNarration.includes('petty cash');

                const isNonTaxable = entry.tax_type === 'non-taxable' || (!hasVat && !isRcm && !isStandard);

                // Include if it contributes to Box 9 or Box 10
                if (hasVat || isRcm || isStandard) return true;

                // Rule 3: Explicitly remove non-taxable petty cash
                if (isNonTaxable && isPettyCash) return false;

                return false;
            });

            // Filter VAT payment entries to include ONLY the bank charges component if it exists
            // This prevents "VAT Payable" (the liability payment) from appearing in the recoverable expenses list
            const vatPaymentEntries = (vatReturnData.expenses_data?.vat_payment_entries || [])
                .filter(entry => (entry.bank_charge || 0) > 0)
                .map(entry => ({
                    ...entry,
                    is_vat_payment: true,
                    entry_id: `vat_p_charge_${entry.entry_id || entry.id}`,
                    vendor_name: 'Bank Charges (VAT Payment)',
                    ledger_name: 'Bank Charges',
                    amount: entry.bank_charge, // Show ONLY the bank charge amount
                    vat_amount: 0, // VAT on bank charges would typically be a separate expense entry if it exists
                    invoice_no: entry.reference_no || entry.number,
                    vat_type: 'standard', // Treat as standard expense for display
                    detailsType: 'vat_payment'
                }));

            // Combine expense entries with filtered VAT payment entries
            const combinedEntries = [...allExpenseEntries, ...vatPaymentEntries];

            // [NEW] Check if there are carry-forward items
            // const carryForwardItems = combinedEntries.filter(e => e.is_carried_forward);
            // Logging removed as per request

            // [FIX] Simplify Logic: Remove Alert and just show the list
            // Strategy:
            // 1. Show all current period items
            // 2. Show Carry-Forward items ONLY if they are explicitly marked as 'claimed: true'
            // 3. Hide Carry-Forward items that are 'claimed: false' (they belong in the Unclaimed Offcanvas)
            // 4. Default filter to 'taxable'

            const finalEntries = combinedEntries.filter(e => {
                // If it's a carry-forward item, only show it if explicitly claimed
                const startDateObj = new Date(startDate);
                const itemDateObj = new Date(e.date);
                const isOld = itemDateObj < startDateObj;

                // Guard: If item is older than period but lacks flag, it's a legacy glitch. Hide it.
                if (isOld && !e.is_carried_forward) {
                    return false;
                }

                if (e.is_carried_forward) {
                    return e.claimed === true;
                }
                // Show all non-carry-forward items
                return true;
            });

            setTaxableFilter('taxable');
            setSelectedDetails({
                type: 'recoverable',
                title: 'Recoverable VAT - Expenses',
                data: finalEntries,
                period_start: startDate,
                period_end: endDate,
                totalAmount: finalEntries.reduce((sum, entry) => sum + (entry.claimed !== false ? (parseFloat(entry.amount) || 0) : 0), 0),
                vatAmount: finalEntries.reduce((sum, entry) => sum + (entry.claimed !== false ? (parseFloat(entry.vat_amount) || 0) : 0), 0),
                originalData: {
                    expenseEntries: allExpenseEntries,
                    vatPaymentEntries: vatPaymentEntries
                }
            });

            setDetailsType('recoverable');
            setShowDetailsOffcanvas(true);

        } catch (error) {
            console.error('Error fetching period transactions:', error);
            Swal.fire('Error!', 'Failed to load expense details', 'error');
        } finally {
            setDetailsLoading(false);
        }
    };

    // Update toggleClaimStatus function
    // Update toggleClaimStatus function
    const toggleClaimStatus = async (entryId, currentStatus, detailsTypeParam, ledgerId = null) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Map UI type to API type
            const apiType = detailsTypeParam === 'recoverable' ? 'expense' : 'sales';

            const response = await axios.post(
                `${API_URL}/toggle-claim`,
                {
                    entry_id: entryId,
                    ledger_id: ledgerId, // Added ledger_id
                    type: apiType,
                    claimed: !currentStatus,
                    period_start: selectedDetails?.period_start || vatReturnForm.period_start || selectedReturn?.period_start,
                    period_end: selectedDetails?.period_end || vatReturnForm.period_end || selectedReturn?.period_end
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true
                }
            );

            // Update local state with server response
            if (response.data && response.data.report_data) {
                console.log("Toggle Claim Response:", response.data);

                // Update the main report data
                const transformedData = transformReportData(response.data.report_data);
                console.log("✅ Transformed Data updated after toggle");
                setVatReturnData(transformedData);

                if (response.data.data) {
                    setSelectedReturn(prev => ({
                        ...prev,
                        ...response.data.data,
                        total_amount: response.data.data.total_amount,
                        net_vat_payable: response.data.data.total_amount
                    }));
                    if (response.data.data.id) {
                        setVatReturns(prev => (prev || []).map(r => r.id === response.data.data.id ? { ...r, ...response.data.data } : r));
                    }
                }

                // [NEW] Update unclaimed items list if present in response
                // [FIX] Do NOT replace entire list from response as it might be incomplete/empty in POST response context
                // Instead, rely on optimistic ID removal which is safer for user experience in this context
                /*
                if (response.data.report_data.unclaimed_previous_items) {
                    setUnclaimedItems(response.data.report_data.unclaimed_previous_items);
                } 
                */

                // Optimistic update: Remove from unclaimed list if we just claimed it (currentStatus was false -> becoming true)
                // Or if we are in the context of the Unclaimed Offcanvas, any action should probably remove it from view until refresh
                if (!currentStatus) {
                    setUnclaimedItems(prev => {
                        const cleanId = (id) => String(id).replace('vat_payment_', '');
                        const targetId = cleanId(entryId);

                        return {
                            ...prev,
                            sales_entries: (prev.sales_entries || []).filter(e => cleanId(e.entry_id) !== targetId),
                            expense_entries: (prev.expense_entries || []).filter(e => cleanId(e.entry_id) !== targetId),
                            vat_payment_entries: (prev.vat_payment_entries || []).filter(e => cleanId(e.entry_id) !== targetId && cleanId(e.id) !== targetId)
                        };
                    });
                }
            }

            // [FIX] Update local state instead of replacing from backend to preserve mixed lists (Current + Unclaimed)
            let updatedEntries = [];
            if (selectedDetails && selectedDetails.data) {
                updatedEntries = selectedDetails.data.map(entry => {
                    if (entry.entry_id === entryId || entry.entry_id === String(entryId).replace('vat_payment_', '')) {
                        return { ...entry, claimed: !currentStatus };
                    }
                    return entry;
                });
            } else {
                // Fallback for edge cases (should not happen in offcanvas view)
                updatedEntries = [];
            }

            // Recalculate totals for offcanvas based on updated entries
            const newTaxableAmount = updatedEntries
                .filter(entry => entry.claimed !== false)
                .reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);

            const newVatAmount = updatedEntries
                .filter(entry => entry.claimed !== false)
                .reduce((sum, entry) => sum + (parseFloat(entry.vat_amount) || 0), 0);

            const newAdjustmentAmount = updatedEntries
                .filter(entry => entry.claimed !== false)
                .reduce((sum, entry) => sum + (parseFloat(entry.adjustment_amount) || 0), 0);

            setSelectedDetails(prev => ({
                ...prev,
                data: updatedEntries,
                totalAmount: newTaxableAmount + newAdjustmentAmount,
                vatAmount: newVatAmount,
                adjustmentTotal: newAdjustmentAmount
            }));

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: `Item ${!currentStatus ? 'claimed' : 'unclaimed'} successfully`,
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error('Error toggling claim status:', error);
            Swal.fire('Error!', 'Failed to update claim status', 'error');
        }
    };


    const formatCurrency = (amount) => {
        const val = parseFloat(amount);
        return new Intl.NumberFormat('en-AE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(isNaN(val) ? 0 : val);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';

        // [FIX] robust date parsing to prevent timezone shifting
        // If it's a date-only string (YYYY-MM-DD), parse it as local time
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
            const dateOnly = dateString.split('T')[0];
            const [year, month, day] = dateOnly.split('-').map(Number);
            // new Date(year, monthIndex, day) creates date in local time
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        }

        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';

        let date;
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
            const dateOnly = dateString.split('T')[0];
            const [year, month, day] = dateOnly.split('-').map(Number);
            date = new Date(year, month - 1, day);
        } else {
            date = new Date(dateString);
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day} ${getMonthName(date.getMonth())} ${year}`;
    };

    const getMonthName = (month) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[month];
    };

    // Backend sometimes sends previous_period as a plain string (e.g. "Q1 2021")
    // and sometimes as a { start_date, end_date } object. Never render the object directly.
    const formatPreviousPeriod = (value) => {
        if (!value) return '-';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            const start = value.start_date || value.period_start;
            const end = value.end_date || value.period_end;
            if (start && end) return `${formatDateDisplay(start)} - ${formatDateDisplay(end)}`;
            if (start) return formatDateDisplay(start);
        }
        return '-';
    };

    const getStatusBadge = (status, item = null) => {
        const isFiled = status === 'submitted' || status === 'paid' || status === 'partially_paid' || Boolean(item?.submitted_at);
        if (isFiled) {
            return <Badge bg="success">Filed</Badge>;
        }
        return <Badge bg="warning" text="dark">Unfiled</Badge>;
    };

    const exportVatReturnToExcel = () => {
        const wb = XLSX.utils.book_new();
        const reportData = [
            ['# Aircom', '', '', ''],
            ['## VAT Return', '', '', ''],
            ['', '', '', ''],
            ['**Basis: Accrual**', '', '', ''],
            [`**From ${vatReturnData.period}**`, '', '', ''],
            ['', '', '', ''],
            ['### VAT on Sales and all other Outputs', '', '', ''],
            ['Box#', 'Description', 'Amount', 'VAT Amount', 'Adjustment'],
        ];

        const emirates = ['a', 'b', 'c', 'd', 'e', 'f', 'g'];
        vatReturnData.vat_on_sales.standard_rated_supplies.forEach((supply, index) => {
            reportData.push([
                `1${emirates[index]}`,
                `Standard rated supplies in ${supply.emirate}`,
                formatCurrency(supply.amount),
                formatCurrency(supply.vat_amount),
                formatCurrency(supply.adjustment)
            ]);
        });

        reportData.push(['2', 'Tax Refunds provided to Tourists under the Tax Refunds for Tourists Scheme',
            formatCurrency(vatReturnData.vat_on_sales.tax_refunds_tourists.amount),
            formatCurrency(vatReturnData.vat_on_sales.tax_refunds_tourists.vat_amount),
            formatCurrency(vatReturnData.vat_on_sales.tax_refunds_tourists.adjustment)
        ]);
        reportData.push(['3', 'Supplies subject to the reverse charge provisions',
            formatCurrency(vatReturnData.vat_on_sales.reverse_charge_supplies.amount),
            formatCurrency(vatReturnData.vat_on_sales.reverse_charge_supplies.vat_amount),
            formatCurrency(vatReturnData.vat_on_sales.reverse_charge_supplies.adjustment)
        ]);
        reportData.push(['4', 'Zero rated supplies',
            formatCurrency(vatReturnData.vat_on_sales.zero_rated_supplies.amount), '', ''
        ]);
        reportData.push(['5', 'Exempt supplies',
            formatCurrency(vatReturnData.vat_on_sales.exempt_supplies.amount), '', ''
        ]);
        reportData.push(['6', 'Goods imported into the UAE',
            formatCurrency(vatReturnData.vat_on_sales.goods_imported.amount),
            formatCurrency(vatReturnData.vat_on_sales.goods_imported.vat_amount), ''
        ]);
        reportData.push(['7', 'Adjustments and additions to goods imported into the UAE',
            formatCurrency(vatReturnData.vat_on_sales.adjustments_imports.amount),
            formatCurrency(vatReturnData.vat_on_sales.adjustments_imports.vat_amount), ''
        ]);
        reportData.push(['8', 'Totals',
            formatCurrency(vatReturnData.vat_on_sales.totals.taxable_amount),
            formatCurrency(vatReturnData.vat_on_sales.totals.tax_amount),
            formatCurrency(vatReturnData.vat_on_sales.totals.adjustment)
        ]);

        reportData.push(['', '', '', '']);
        reportData.push(['### VAT on Expenses and all other Inputs', '', '', '']);
        reportData.push(['Box#', 'Description', 'Amount', 'Recoverable VAT Amount', 'Adjustment']);

        reportData.push(['9', 'Standard rated expenses',
            formatCurrency(vatReturnData.vat_on_expenses.standard_rated_expenses.amount),
            formatCurrency(vatReturnData.vat_on_expenses.standard_rated_expenses.recoverable_vat),
            formatCurrency(vatReturnData.vat_on_expenses.standard_rated_expenses.adjustment)
        ]);
        reportData.push(['10', 'Supplies subject to the reverse charge provisions',
            formatCurrency(vatReturnData.vat_on_expenses.reverse_charge_supplies.amount),
            formatCurrency(vatReturnData.vat_on_expenses.reverse_charge_supplies.recoverable_vat),
            formatCurrency(vatReturnData.vat_on_expenses.reverse_charge_supplies.adjustment)
        ]);
        reportData.push(['11', 'Totals',
            formatCurrency(vatReturnData.vat_on_expenses.totals.taxable_amount),
            formatCurrency(vatReturnData.vat_on_expenses.totals.recoverable_vat_amount),
            formatCurrency(vatReturnData.vat_on_expenses.totals.adjustment)
        ]);

        reportData.push(['', '', '', '']);
        reportData.push(['### Net VAT due', '', '', '']);
        reportData.push(['Box#', 'Description', 'VAT Amount', '']);
        reportData.push(['12', 'Total value of due tax for the period',
            formatCurrency(vatReturnData.net_vat_due.total_due_tax), ''
        ]);
        reportData.push(['13', 'Total value of recoverable tax for the period',
            formatCurrency(vatReturnData.net_vat_due.total_recoverable_tax), ''
        ]);
        reportData.push(['14', 'Net VAT payable (or reclaimable) for the period',
            formatCurrency(vatReturnData.net_vat_due.net_vat_payable), ''
        ]);
        reportData.push(['15', 'Do you wish to request a refund for the above amount of reclaimable VAT?',
            vatReturnData.net_vat_due.request_refund ? 'Yes' : 'No', ''
        ]);

        const reportWs = XLSX.utils.aoa_to_sheet(reportData);
        XLSX.utils.book_append_sheet(wb, reportWs, 'VAT Return');

        const fileName = `VAT_Return_${vatReturnForm.period_start}_to_${vatReturnForm.period_end}.xlsx`;
        XLSX.writeFile(wb, fileName);

        Swal.fire('Success!', 'VAT return exported to Excel', 'success');
    };

    const exportToPDF = () => {
        Swal.fire('Info', 'PDF export feature will be implemented soon', 'info');
    };

    const handleDownloadTaxInvoicePDF = async (id, refNo) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios({
                url: `${backendUrl}/api/taxinvoice/${id}/download`,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            saveAs(blob, `tax-invoice-${refNo}.pdf`);
        } catch (error) {
            console.error('Error downloading tax invoice:', error);
            Swal.fire('Error', 'Failed to download tax invoice PDF', 'error');
        }
    };

    const printReport = () => {
        window.print();
    };

    const ZohoStyleReport = () => (
        <Container fluid className="p-4 bg-white">
            <style>{`
                .audited-row > td {
                    background-color: #f0f8ff !important;
                }
            `}</style>
            {/* Report Header with Actions */}
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <div>
                    <Button
                        variant="light"
                        onClick={() => setShowReportPage(false)}
                        className="d-flex align-items-center gap-2 mb-2"
                    >
                        <FiArrowLeft /> Back to List
                    </Button>
                    <h1 className="text-dark fw-bold mb-0">VAT Return Report</h1>
                    <div className="text-muted d-flex align-items-center gap-3 mt-2">
                        <span className="d-flex align-items-center gap-1">
                            <FiCalendar />  {vatReturnData.period ||
                                (selectedReturn?.period_start && selectedReturn?.period_end
                                    ? `${formatDateDisplay(selectedReturn.period_start)} To ${formatDateDisplay(selectedReturn.period_end)}`
                                    : 'Period not available')}
                        </span>
                        <span className="d-flex align-items-center gap-1">
                            <FiFileText /> {vatReturnData.basis} Basis
                        </span>
                    </div>
                </div>

                {/* Action Buttons - Moved to top */}
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-warning"
                        onClick={async () => {
                            const initialUnclaimed = JSON.parse(JSON.stringify(vatReturnData.unclaimed_previous_items || { sales_entries: [], expense_entries: [], vat_payment_entries: [] }));
                            setUnclaimedItems(initialUnclaimed);
                            setShowUnclaimedOffcanvas(true);

                            if (!selectedReturn) return;

                            setIsFetchingLateItems(true);
                            try {
                                const token = JSON.parse(localStorage.getItem('user-info'))?.token;
                                const response = await axios.get(`${API_URL}/period-transactions`, {
                                    params: {
                                        period_start: selectedReturn.period_start,
                                        period_end: selectedReturn.period_end,
                                        include_unclaimed: false
                                    },
                                    headers: {
                                        Authorization: `Bearer ${token}`
                                    }
                                });
                                const freshData = response.data;
                                let items = { ...initialUnclaimed };

                                // Find late sales
                                if (freshData?.sales_entries) {
                                    const savedSales = vatReturnData.sales_data?.sales_entries || [];
                                    const savedIds = new Set(savedSales.map(e => e.entry_id));
                                    const lateSales = freshData.sales_entries.filter(e => !savedIds.has(e.entry_id));
                                    lateSales.forEach(e => e.is_late_added = true);
                                    items.sales_entries = [...items.sales_entries, ...lateSales];
                                }

                                // Find late expenses
                                if (freshData?.expense_entries) {
                                    const savedExp = vatReturnData.expenses_data?.expense_entries || [];
                                    const savedIds = new Set(savedExp.map(e => e.entry_id));
                                    const lateExp = freshData.expense_entries.filter(e => !savedIds.has(e.entry_id));
                                    lateExp.forEach(e => e.is_late_added = true);
                                    items.expense_entries = [...items.expense_entries, ...lateExp];
                                }

                                // Find late vat payments
                                if (freshData?.vat_payment_entries) {
                                    const savedVat = vatReturnData.expenses_data?.vat_payment_entries || [];
                                    const savedIds = new Set(savedVat.map(e => e.entry_id));
                                    const lateVat = freshData.vat_payment_entries.filter(e => !savedIds.has(e.entry_id));
                                    lateVat.forEach(e => e.is_late_added = true);
                                    items.vat_payment_entries = [...items.vat_payment_entries, ...lateVat];
                                }

                                setUnclaimedItems(items);
                            } catch (error) {
                                console.error("Failed to fetch late-added items", error);
                            } finally {
                                setIsFetchingLateItems(false);
                            }
                        }}
                        className="d-flex align-items-center gap-2"
                    >
                        <FiFilter /> Unclaimed
                        <Badge bg="warning" text="dark" pill>
                            {getUnclaimedTotal(vatReturnData.unclaimed_previous_count)}
                        </Badge>
                    </Button>
                    {/* <Button
                        variant="outline-primary"
                        onClick={exportVatReturnToExcel}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaFileExcel /> Excel
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={exportToPDF}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaRegFilePdf /> PDF
                    </Button>
                    <Button
                        variant="outline-secondary"
                        onClick={printReport}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPrint /> Print
                    </Button> */}
                </div>
            </div>

            {/* Summary Cards */}
            <Row className="mb-4">
                <Col md={4}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="text-muted mb-2">VAT Due</h6>
                                    <h3 className="fw-bold text-primary">
                                        AED {formatCurrency(vatReturnData.net_vat_due.total_due_tax)}
                                    </h3>
                                </div>
                                <div className="bg-primary bg-opacity-10 p-2 rounded">
                                    <FiDollarSign size={20} className="text-primary" />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="text-muted mb-2">Recoverable VAT</h6>
                                    <h3 className="fw-bold text-success">
                                        AED {formatCurrency(vatReturnData.net_vat_due.total_recoverable_tax)}
                                    </h3>
                                </div>
                                <div className="bg-success bg-opacity-10 p-2 rounded">
                                    <FiDollarSign size={20} className="text-success" />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className={`border-0 shadow-sm h-100 ${vatReturnData.net_vat_due.net_vat_payable >= 0 ? 'border-primary' : 'border-danger'}`}>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h6 className="text-muted mb-2">Net VAT Payable</h6>
                                    <h3 className={`fw-bold ${vatReturnData.net_vat_due.net_vat_payable >= 0 ? 'text-primary' : 'text-danger'}`}>
                                        AED {formatCurrency(vatReturnData.net_vat_due.net_vat_payable)}
                                    </h3>
                                </div>
                                <div className={`${vatReturnData.net_vat_due.net_vat_payable >= 0 ? 'bg-primary' : 'bg-danger'} bg-opacity-10 p-2 rounded`}>
                                    <FiDollarSign size={20} className={vatReturnData.net_vat_due.net_vat_payable >= 0 ? 'text-primary' : 'text-danger'} />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* VAT on Sales Section */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                    <h5 className="mb-0 text-primary">
                        <FiChevronRight className="me-2" />
                        VAT on Sales and all other Outputs
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-sales-gradient">
                                <tr>
                                    <th width="5%" className="text-muted">Box#</th>
                                    <th width="55%" className="text-muted">Description</th>
                                    <th width="15%" className="text-muted text-end">Amount (AED)</th>
                                    <th width="15%" className="text-muted text-end">VAT Amount (AED)</th>
                                    <th width="10%" className="text-muted text-end">Adjustment (AED)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vatReturnData.vat_on_sales.standard_rated_supplies.map((supply, index) => (
                                    <tr key={supply.emirate}>
                                        <td className="fw-medium">1{String.fromCharCode(97 + index)}</td>
                                        <td
                                            className="d-flex align-items-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleEmirateClick(supply)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                    <span className="text-primary small fw-bold">{index + 1}</span>
                                                </div>
                                                Standard rated supplies in <span className="fw-medium ms-1">{supply.emirate}</span>
                                            </div>
                                        </td>
                                        <td className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleEmirateClick(supply)}>{formatCurrency(supply.amount)}</td>
                                        <td className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleEmirateClick(supply)}>{formatCurrency(supply.vat_amount)}</td>
                                        <td className="text-end">{formatCurrency(supply.adjustment)}</td>
                                    </tr>
                                ))}


                                <tr>
                                    <td className="fw-medium">2</td>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                <span className="text-primary small fw-bold">2</span>
                                            </div>
                                            Tax Refunds provided to Tourists
                                        </div>
                                    </td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.tax_refunds_tourists.amount)}</td>
                                    <td className="text-end text-danger">{formatCurrency(vatReturnData.vat_on_sales.tax_refunds_tourists.vat_amount)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.tax_refunds_tourists.adjustment)}</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">3</td>
                                    <td
                                        className="ps-4"
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleRcmClick}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                <span className="text-primary small fw-bold">3</span>
                                            </div>
                                            Supplies subject to the reverse charge provisions
                                        </div>
                                    </td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.reverse_charge_supplies.amount)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.reverse_charge_supplies.vat_amount)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.reverse_charge_supplies.adjustment)}</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">4</td>
                                    <td
                                        className="d-flex align-items-center"
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleZeroRatedClick}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                <span className="text-primary small fw-bold">4</span>
                                            </div>
                                            Zero rated supplies
                                        </div>
                                    </td>
                                    <td className="text-end" style={{ cursor: 'pointer' }} onClick={handleZeroRatedClick}>{formatCurrency(vatReturnData.vat_on_sales.zero_rated_supplies.amount)}</td>
                                    <td className="text-end text-muted">-</td>
                                    <td className="text-end text-muted">-</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">5</td>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                <span className="text-primary small fw-bold">5</span>
                                            </div>
                                            Exempt supplies
                                        </div>
                                    </td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.exempt_supplies.amount)}</td>
                                    <td className="text-end text-muted">-</td>
                                    <td className="text-end text-muted">-</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">6</td>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                <span className="text-primary small fw-bold">6</span>
                                            </div>
                                            Goods imported into the UAE
                                        </div>
                                    </td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.goods_imported.amount)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.goods_imported.vat_amount)}</td>
                                    <td className="text-end text-muted">-</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">7</td>
                                    <td className="ps-4">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                                                <span className="text-primary small fw-bold">7</span>
                                            </div>
                                            Adjustments to goods imported into the UAE
                                        </div>
                                    </td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.adjustments_imports.amount)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_sales.adjustments_imports.vat_amount)}</td>
                                    <td className="text-end text-muted">-</td>
                                </tr>

                                <tr className="table-primary bg-primary bg-opacity-10 shadow-sm">
                                    <td className="fw-bold">8</td>
                                    <td className="fw-bold">Totals</td>
                                    <td className="text-end fw-bold">
                                        {formatCurrency(
                                            (vatReturnData.vat_on_sales.totals?.taxable_amount || 0) +
                                            (vatReturnData.vat_on_sales.tax_refunds_tourists?.amount || 0) +
                                            (vatReturnData.vat_on_sales.reverse_charge_supplies?.amount || 0) +
                                            (vatReturnData.vat_on_sales.zero_rated_supplies?.amount || 0) +
                                            (vatReturnData.vat_on_sales.exempt_supplies?.amount || 0) +
                                            (vatReturnData.vat_on_sales.goods_imported?.amount || 0) +
                                            (vatReturnData.vat_on_sales.adjustments_imports?.amount || 0)
                                        )}
                                    </td>
                                    <td className="text-end fw-bold">
                                        {formatCurrency(
                                            (vatReturnData.vat_on_sales.totals?.tax_amount || 0) -
                                            (vatReturnData.vat_on_sales.tax_refunds_tourists?.vat_amount || 0) +
                                            (vatReturnData.vat_on_sales.reverse_charge_supplies?.vat_amount || 0) +
                                            (vatReturnData.vat_on_sales.goods_imported?.vat_amount || 0) +
                                            (vatReturnData.vat_on_sales.adjustments_imports?.vat_amount || 0)
                                        )}
                                    </td>
                                    <td className="text-end fw-bold">
                                        {formatCurrency(
                                            (vatReturnData.vat_on_sales.totals?.adjustment || 0) +
                                            (vatReturnData.vat_on_sales.tax_refunds_tourists?.adjustment || 0) +
                                            (vatReturnData.vat_on_sales.reverse_charge_supplies?.adjustment || 0)
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>


            {/* VAT on Expenses Section */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                    <h5 className="mb-0 text-primary">
                        <FiChevronRight className="me-2" />
                        VAT on Expenses and all other Inputs
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-sales-gradient">
                                <tr>
                                    <th width="5%" className="text-muted small">Box#</th>
                                    <th width="55%" className="text-muted small">Description</th>
                                    <th width="15%" className="text-muted small text-end">Amount (AED)</th>
                                    <th width="15%" className="text-muted small text-end">Recoverable VAT (AED)</th>
                                    <th width="10%" className="text-muted small text-end">Adjustment (AED)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="fw-medium">9</td>
                                    <td
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleRecoverableClick}
                                        className="text-dark"
                                    >Standard rated expenses</td>
                                    <td className="text-end" onClick={handleRecoverableClick}>{formatCurrency(vatReturnData.vat_on_expenses.standard_rated_expenses.amount)}</td>
                                    <td className="text-end text-success" onClick={handleRecoverableClick}>{formatCurrency(vatReturnData.vat_on_expenses.standard_rated_expenses.recoverable_vat)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_expenses.standard_rated_expenses.adjustment)}</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">10</td>
                                    <td
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleRcmClick}
                                    >Supplies subject to the reverse charge provisions</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_expenses.reverse_charge_supplies.amount)}</td>
                                    <td className="text-end text-success">{formatCurrency(vatReturnData.vat_on_expenses.reverse_charge_supplies.recoverable_vat)}</td>
                                    <td className="text-end">{formatCurrency(vatReturnData.vat_on_expenses.reverse_charge_supplies.adjustment)}</td>
                                </tr>

                                <tr className="table-sales-gradient">
                                    <td className="fw-bold">11</td>
                                    <td className="fw-bold">Totals</td>
                                    <td className="text-end fw-bold">{formatCurrency(vatReturnData.vat_on_expenses.totals.taxable_amount)}</td>
                                    <td className="text-end fw-bold text-success">{formatCurrency(vatReturnData.vat_on_expenses.totals.recoverable_vat_amount)}</td>
                                    <td className="text-end fw-bold">{formatCurrency(vatReturnData.vat_on_expenses.totals.adjustment)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>

            {/* Net VAT Due Section */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-white border-0 py-3">
                    <h5 className="mb-0 text-primary">
                        <FiChevronRight className="me-2" />
                        Net VAT Due
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-sales-gradient">
                                <tr>
                                    <th width="5%" className="text-muted small">Box#</th>
                                    <th width="70%" className="text-muted small">Description</th>
                                    <th width="25%" className="text-muted small text-end">Amount (AED)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="fw-medium">12</td>
                                    <td>Total value of due tax for the period</td>
                                    <td className="text-end fw-bold text-primary">{formatCurrency(vatReturnData.net_vat_due.total_due_tax)}</td>
                                </tr>

                                <tr>
                                    <td className="fw-medium">13</td>
                                    <td>Total value of recoverable tax for the period</td>
                                    <td className="text-end fw-bold text-success">{formatCurrency(vatReturnData.net_vat_due.total_recoverable_tax)}</td>
                                </tr>

                                <tr className={`${vatReturnData.net_vat_due.net_vat_payable >= 0 ? 'table-primary' : 'table-danger'} fw-bold`}>
                                    <td className="fw-bold">14</td>
                                    <td className="fw-bold">Net VAT payable (or reclaimable) for the period</td>
                                    <td className="text-end fw-bold fs-5">
                                        {formatCurrency(vatReturnData.net_vat_due.net_vat_payable)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>

            {/* Footer */}
            <div className="border-top pt-3 mt-4">
                <div className="row">
                    <div className="col-md-6">
                        <div className="text-muted small">
                            <div className="d-flex align-items-center gap-1 mb-1">
                                <FiCalendar /> Generated on: {new Date().toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <FiFileText /> Report ID: {selectedReturn?.id || 'N/A'}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <div className="text-muted small">
                            All amounts are in UAE Dirhams (AED)
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );

    return (
        <div className="main-content">
            {showReportPage ? (
                <div className="page-content">
                    <div className="container-fluid">
                        <Row>
                            <div className="col-12">
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-0">
                                        <ZohoStyleReport />
                                    </Card.Body>
                                </Card>
                            </div>
                        </Row>
                    </div>
                </div>
            ) : (
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <Row className="mb-3">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <div>
                                        <h4 className="mb-sm-0 font-size-18">VAT Return Management</h4>
                                        <p className="text-muted mb-0">Generate and manage VAT return reports</p>
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleOpenGenerateModal}
                                        className="d-flex align-items-center gap-2"
                                    >
                                        <FiFileText /> Generate New Report
                                    </Button>
                                </div>
                            </div>
                        </Row>

                        <Tabs defaultActiveKey="generated" className="mb-4 custom-report-tabs" id="main-vat-tabs">
                            <Tab eventKey="generated" title={<><FiFileText className="me-2" />Generated Returns</>}>
                                <div className="pt-2">
                                    {/* VAT Returns Table */}
                                    <Row>
                                        <div className="col-12">
                                            <Card className="border-0 shadow-sm">
                                                <Card.Header className="bg-white border-0 py-3">
                                                    <div className="d-flex justify-content-between align-items-center w-100">
                                                        <h5 className="mb-0 text-primary">Generated VAT Returns</h5>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={fetchVatReturns}
                                                            disabled={loading}
                                                            className="text-muted p-0 ms-auto"
                                                            title="Refresh Returns"
                                                        >
                                                            <FiRefreshCw size={18} className={loading ? "spin" : ""} />
                                                        </Button>
                                                    </div>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="table-responsive">
                                                        <Table hover className="align-middle mb-0">
                                                            <thead className='table-sales-gradient'>
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>Period</th>
                                                                    <th>VAT Payable</th>
                                                                    <th>Variation</th>
                                                                    <th>Status</th>
                                                                    <th>Generated On</th>
                                                                    <th>Payment Status</th>
                                                                    <th className="text-center">Attachment</th>
                                                                    <th className="text-end" style={{ minWidth: '150px' }}>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {loading ? (
                                                                    <tr>
                                                                        <td colSpan="9" className="text-center py-5">
                                                                            {/* Loading handled by global loader */}
                                                                        </td>
                                                                    </tr>
                                                                ) : vatReturns.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="9" className="text-center py-5 text-muted">
                                                                            <div className="py-4">
                                                                                <FiFileText size={48} className="mb-3 text-muted" />
                                                                                <h5>No VAT returns generated yet</h5>
                                                                                <p className="text-muted">Generate your first VAT return report</p>
                                                                                <Button
                                                                                    variant="primary"
                                                                                    onClick={handleOpenGenerateModal}
                                                                                    className="mt-2"
                                                                                >
                                                                                    Generate Report
                                                                                </Button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    vatReturns.map((item, index) => (
                                                                        <tr key={item.id}>
                                                                            <td>
                                                                                <div className="fw-medium">{index + 1}</div>
                                                                            </td>
                                                                            <td
                                                                                onClick={() => viewVatReturn(item)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                <div className="d-flex flex-column">
                                                                                    <strong className="text-primary">{formatDate(item.period_start)}</strong>
                                                                                    <small className="text-muted">to {formatDate(item.period_end)}</small>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`fw-bold ${item.net_vat_payable >= 0 ? 'text-primary' : 'text-danger'}`}>
                                                                                    AED {formatCurrency(item.net_vat_payable)}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                {(item.output_variation_amount || item.input_variation_amount) ? (
                                                                                    <span className="text-primary text-decoration-underline fw-medium" style={{ cursor: 'pointer' }} onClick={() => {
                                                                                        setSelectedVariationDetails(item);
                                                                                        setShowVariationDetailsOffcanvas(true);
                                                                                    }}>
                                                                                        AED {formatCurrency((parseFloat(item.output_variation_amount) || 0) - (parseFloat(item.input_variation_amount) || 0))}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-muted">-</span>
                                                                                )}
                                                                            </td>
                                                                            <td>{getStatusBadge(item.payment_status, item)}</td>
                                                                            <td>{formatDate(item.submitted_at)}</td>
                                                                            <td>
                                                                                {Number(item.paid_amount) > 0 && Number(item.remaining_amount) > 0 ? (
                                                                                    <Badge bg="warning">Partially Paid</Badge>
                                                                                ) : item.is_paid || (Number(item.paid_amount) > 0 && Number(item.remaining_amount) === 0) ? (
                                                                                    <Badge bg="success">Paid</Badge>
                                                                                ) : (
                                                                                    <Badge bg="secondary">Unpaid</Badge>
                                                                                )}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {item.attachment ? (
                                                                                    <span
                                                                                        className="text-primary d-flex align-items-center justify-content-center gap-1"
                                                                                        style={{ cursor: 'pointer', fontWeight: 500 }}
                                                                                        onClick={() => {
                                                                                            const backendUrl = process.env.REACT_APP_BACKEND_URL;
                                                                                            let cleanPath = item.attachment.startsWith('/') ? item.attachment.substring(1) : item.attachment;
                                                                                            if (!cleanPath.startsWith('storage/')) {
                                                                                                cleanPath = `storage/${cleanPath}`;
                                                                                            }
                                                                                            const fileUrl = `${backendUrl}/${cleanPath}`;
                                                                                            setDocUrl(fileUrl);
                                                                                            setDownloadUrl(`${backendUrl}/api/vat-payments/${item.id}/download`);
                                                                                            setDocTitle(`VAT Document - ${formatDate(item.period_start)}`);
                                                                                            setShowDocOffcanvas(true);
                                                                                            setSelectedDetails(null);
                                                                                        }}
                                                                                    >
                                                                                        <FiPaperclip size={14} /> View Document
                                                                                    </span>
                                                                                ) : (
                                                                                    ""
                                                                                )}
                                                                            </td>
                                                                            <td className='text-end'>
                                                                                <div className="dropdown">
                                                                                    <i
                                                                                        className="mdi mdi-dots-horizontal font-size-18"
                                                                                        data-bs-toggle="dropdown"
                                                                                        aria-expanded="false"
                                                                                        style={{ cursor: "pointer" }}
                                                                                    ></i>
                                                                                    <ul className="dropdown-menu">
                                                                                        <li>
                                                                                            <button
                                                                                                className="dropdown-item "
                                                                                                onClick={() => viewVatReturn(item)}
                                                                                            >
                                                                                                <FiEye size={14} className="text-primary" />
                                                                                                View Report
                                                                                            </button>
                                                                                        </li>

                                                                                        {item.attachment ? (
                                                                                            <li>
                                                                                                <button
                                                                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                                                                    onClick={() => {
                                                                                                        const backendUrl = process.env.REACT_APP_BACKEND_URL;
                                                                                                        let cleanPath = item.attachment.startsWith('/') ? item.attachment.substring(1) : item.attachment;
                                                                                                        if (!cleanPath.startsWith('storage/')) {
                                                                                                            cleanPath = `storage/${cleanPath}`;
                                                                                                        }
                                                                                                        const fileUrl = `${backendUrl}/${cleanPath}`;
                                                                                                        setDocUrl(fileUrl);
                                                                                                        setDownloadUrl(`${backendUrl}/api/vat-payments/${item.id}/download`);
                                                                                                        setDocTitle(`VAT Document - ${formatDate(item.period_start)}`);
                                                                                                        setShowDocOffcanvas(true);
                                                                                                        // Ensure details offcanvas doesn't re-open for this case
                                                                                                        setSelectedDetails(null);
                                                                                                    }}
                                                                                                >
                                                                                                    <FiFileText size={14} className="text-success" />
                                                                                                    View Document
                                                                                                </button>
                                                                                            </li>
                                                                                        ) : (
                                                                                            <li>
                                                                                                <button
                                                                                                    className="dropdown-item d-flex align-items-center gap-2"
                                                                                                    onClick={() => document.getElementById(`main-vat-upload-${item.id}`).click()}
                                                                                                >
                                                                                                    <FiUpload size={14} className="text-secondary" />
                                                                                                    Upload Document
                                                                                                </button>
                                                                                                <input
                                                                                                    type="file"
                                                                                                    id={`main-vat-upload-${item.id}`}
                                                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                                                    style={{ display: 'none' }}
                                                                                                    onChange={async (e) => {
                                                                                                        const file = e.target.files[0];
                                                                                                        if (!file) return;

                                                                                                        const formData = new FormData();
                                                                                                        formData.append('document', file);

                                                                                                        try {
                                                                                                            const userInfo = JSON.parse(localStorage.getItem('user-info'));
                                                                                                            const origin = localStorage.getItem('user-origin');
                                                                                                            const token = userInfo?.token;
                                                                                                            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

                                                                                                            const response = await axios.post(
                                                                                                                `${backendUrl}/api/vat-payments/${item.id}/upload`,
                                                                                                                formData,
                                                                                                                {
                                                                                                                    headers: {
                                                                                                                        Authorization: `Bearer ${token}`,
                                                                                                                        'Tenant-Origin': origin,
                                                                                                                        'Content-Type': 'multipart/form-data'
                                                                                                                    }
                                                                                                                }
                                                                                                            );

                                                                                                            Swal.fire({
                                                                                                                toast: true,
                                                                                                                position: 'top-end',
                                                                                                                icon: 'success',
                                                                                                                title: 'Document uploaded successfully',
                                                                                                                showConfirmButton: false,
                                                                                                                timer: 2000
                                                                                                            });

                                                                                                            // Refresh the list
                                                                                                            fetchVatReturns();
                                                                                                        } catch (error) {
                                                                                                            console.error('Upload error:', error);
                                                                                                            Swal.fire('Error', 'Failed to upload document', 'error');
                                                                                                        }
                                                                                                    }}
                                                                                                />
                                                                                            </li>
                                                                                        )}

                                                                                        {item.payment_status === 'generated' && !item.submitted_at && !item.is_paid && (
                                                                                            <li>
                                                                                                <button
                                                                                                    className="dropdown-item d-flex align-items-center gap-2 text-primary"
                                                                                                    onClick={() => openFillableModal(item)}
                                                                                                >
                                                                                                    <FiCheckCircle size={14} />
                                                                                                    Mark as Filed
                                                                                                </button>
                                                                                            </li>
                                                                                        )}

                                                                                        <li>
                                                                                            <button
                                                                                                className="dropdown-item d-flex align-items-center gap-2 text-info"
                                                                                                onClick={() => handleOpenVariation(item)}
                                                                                            >
                                                                                                <FiEdit size={14} />
                                                                                                Add Variation
                                                                                            </button>
                                                                                        </li>
                                                                                        <li>
                                                                                            <hr className="dropdown-divider" />
                                                                                        </li>
                                                                                        <li>
                                                                                            <button
                                                                                                className="dropdown-item d-flex align-items-center gap-2 text-danger"
                                                                                                onClick={() => deleteVatReturn(item)}
                                                                                            >
                                                                                                <FiTrash2 size={14} />
                                                                                                Delete
                                                                                            </button>
                                                                                        </li>
                                                                                    </ul>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    </Row>
                                </div>
                            </Tab>

                            <Tab eventKey="unclaimed" title={<><FiFilter className="me-2" />Unclaimed CarryForward Items </>}>
                                <div className="pt-2">
                                    {/* Unclaimed Summary Cards */}


                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-primary">Unclaimed CarryForward Items</h5>
                                            <Button variant="outline-primary" size="sm" onClick={fetchGlobalListData} disabled={globalLoading}>Refresh List</Button>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="table-responsive">
                                                <Table hover className="align-middle border-0">
                                                    <thead className="table-sales-gradient">
                                                        <tr>
                                                            <th className="border-0 text-muted small">Date</th>
                                                            <th className="border-0 text-muted small">Ref/Inv No</th>
                                                            <th className="border-0 text-muted small">Client/Vendor</th>
                                                            <th className="border-0 text-muted small text-end">Amount</th>
                                                            <th className="border-0 text-muted small text-end">VAT Amount</th>
                                                            <th className="border-0 text-muted small text-center">Previous Period</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {globalLoading ? (
                                                            <tr><td colSpan="7" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                                                        ) : [
                                                            ...(globalUnclaimed.sales_entries?.map(e => ({ ...e, ui_type: 'Sales' })) || []),
                                                            ...(globalUnclaimed.expense_entries?.map(e => ({ ...e, ui_type: 'Expense' })) || []),
                                                            ...(globalUnclaimed.vat_payment_entries?.map(e => ({ ...e, ui_type: 'VAT Payment' })) || [])
                                                        ].length === 0 ? (
                                                            <tr><td colSpan="7" className="text-center py-5 text-muted italic">No unclaimed items found.</td></tr>
                                                        ) : [
                                                            ...(globalUnclaimed.sales_entries?.map(e => ({ ...e, ui_type: 'Sales' })) || []),
                                                            ...(globalUnclaimed.expense_entries?.map(e => ({ ...e, ui_type: 'Expense' })) || []),
                                                            ...(globalUnclaimed.vat_payment_entries?.map(e => ({ ...e, ui_type: 'VAT Payment' })) || [])
                                                        ].sort((a, b) => new Date(b.date) - new Date(a.date)).map((item, idx) => (
                                                            <tr key={idx}>
                                                                <td><small>{formatDate(item.date)}</small></td>
                                                                {/* <td>
                                                                    {item.ui_type === 'Sales' ? (
                                                                        <Badge bg="primary" className="bg-opacity-10 text-primary border-primary border">Output</Badge>
                                                                    ) : (
                                                                        <Badge bg="success" className="bg-opacity-10 text-success border-success border">Recoverable</Badge>
                                                                    )}
                                                                </td> */}
                                                                <td className="fw-medium">{item.ref_no || item.invoice_no || item.number || '-'}</td>
                                                                <td>{(item.ui_type === 'VAT Payment' || item.is_vat_payment || item.vat_type === 'vat_payment') ? 'VAT Payable' : (item.client_name || (item.vendor_name === 'FTA / Government' ? (item.ledger_name || item.vendor_name) : item.vendor_name) || item.credit_ledger || item.ledger_name || '-')}</td>
                                                                <td className="text-end fw-semibold">
                                                                    {formatCurrency((item.ui_type === 'VAT Payment' || item.is_vat_payment || item.vat_type === 'vat_payment') ? 0 : item.amount)}
                                                                </td>
                                                                <td className={`text-end fw-bold ${item.ui_type === 'Sales' ? 'text-primary' : 'text-success'}`}>
                                                                    {formatCurrency(item.vat_amount)}
                                                                </td>
                                                                <td className="text-center"><small className="text-muted">{formatPreviousPeriod(item.previous_period)}</small></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Tab>

                            <Tab eventKey="usd_expenses" title={<>Unfiltered RCM Vat Returns </>}>
                                <div className="pt-2">
                                    <Card className="border-0 shadow-sm">
                                        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-primary">Unfiltered RCM VAT Returns</h5>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="small text-muted">From:</span>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            value={dayjs(usdFilterStart)}
                                                            onChange={(newValue) => setUsdFilterStart(newValue ? newValue.format('YYYY-MM-DD') : '')}
                                                            slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-input': { fontSize: '0.8rem', py: 1 } } } }}
                                                            format="DD-MM-YYYY"
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="small text-muted">To:</span>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            value={dayjs(usdFilterEnd)}
                                                            onChange={(newValue) => setUsdFilterEnd(newValue ? newValue.format('YYYY-MM-DD') : '')}
                                                            slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-input': { fontSize: '0.8rem', py: 1 } } } }}
                                                            format="DD-MM-YYYY"
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={fetchGlobalListData}
                                                    disabled={globalLoading}
                                                    className="d-flex align-items-center gap-2"
                                                >
                                                    {globalLoading ? <div className="spinner-border spinner-border-sm"></div> : <FiFilter />}
                                                    Apply Filter
                                                </Button>
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="table-responsive">
                                                <Table hover className="align-middle border-0">
                                                    <thead className="table-sales-gradient">
                                                        <tr>
                                                            <th className="border-0 text-muted small">Date</th>
                                                            <th className="border-0 text-muted small">Invoice No</th>
                                                            <th className="border-0 text-muted small">Vendor Name</th>
                                                            <th className="border-0 text-muted small text-end">Amount</th>
                                                            <th className="border-0 text-muted small text-end">VAT Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {globalLoading ? (
                                                            <tr><td colSpan="7" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                                                        ) : globalUsdExpenses.length === 0 ? (
                                                            <tr><td colSpan="7" className="text-center py-5 text-muted italic">No expenses found.</td></tr>
                                                        ) : globalUsdExpenses.sort((a, b) => new Date(b.date) - new Date(a.date)).map((item, idx) => (
                                                            <tr key={idx}>
                                                                <td><small>{formatDate(item.date)}</small></td>
                                                                <td className="fw-medium">{item.invoice_no || item.number || '-'}</td>
                                                                <td>
                                                                    {item.vendor_name || item.credit_ledger || item.ledger_name}
                                                                </td>
                                                                <td className="text-end fw-semibold">{formatCurrency(item.amount)}</td>
                                                                <td className="text-end text-success">{formatCurrency(item.vat_amount)}</td>

                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            )}

            {/* Generate Modal */}
            <Offcanvas show={showGenerateModal} onHide={() => setShowGenerateModal(false)} placement='end' style={{ width: '480px' }}>
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="d-flex align-items-center gap-2">
                        <FiFileText /> Generate VAT Return
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form>
                        <div className="mb-4">
                            <h6 className="text-muted mb-3">Select Period</h6>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                                        {/* <Form.Control
                                            type="date"
                                            name="period_start"
                                            value={vatReturnForm.period_start}
                                            onChange={handleVatReturnFormChange}
                                            required
                                            className="border-1"
                                        /> */}

                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                views={["year", "month", "day"]}
                                                format="DD-MM-YYYY"
                                                density="compact"
                                                className='form-feild form-control'
                                                value={
                                                    vatReturnForm.period_start && dayjs(vatReturnForm.period_start).isValid()
                                                        ? dayjs(vatReturnForm.period_start)
                                                        : null
                                                }
                                                onChange={(newValue) => handleVatReturnFormChange('period_start', newValue)}

                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        className: "form-feild",
                                                    },
                                                    popper: {
                                                        disablePortal: true,   // ✅ KEY LINE
                                                        sx: {
                                                            zIndex: 2000,        // ✅ ensure above offcanvas content
                                                        },
                                                    },
                                                }}
                                            />

                                        </LocalizationProvider>

                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                views={["year", "month", "day"]}
                                                format="DD-MM-YYYY"
                                                density="compact"
                                                className='form-feild form-control'
                                                value={
                                                    vatReturnForm.period_end && dayjs(vatReturnForm.period_end).isValid()
                                                        ? dayjs(vatReturnForm.period_end)
                                                        : null
                                                }
                                                onChange={(newValue) => handleVatReturnFormChange('period_end', newValue)}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        className: "form-feild",
                                                    },
                                                    popper: {
                                                        disablePortal: true,
                                                        sx: {
                                                            zIndex: 2000,
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </Form>
                </Offcanvas.Body>
                <div className="p-3 border-top bg-light">
                    <div className="d-flex justify-content-between">
                        <Button variant="light" onClick={() => setShowGenerateModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={fetchVatReturnReport}
                            disabled={reportLoading}
                            className="d-flex align-items-center gap-2"
                        >
                            {reportLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm"></span>
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <FiFileText /> Generate Report
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Offcanvas>

            {/* Fillable Date Offcanvas */}
            <Offcanvas show={showFillableModal} onHide={() => setShowFillableModal(false)} placement='end' style={{ width: '480px' }}>
                <Offcanvas.Header closeButton className="bg-light text-dark">
                    <Offcanvas.Title>
                        <i className="mdi mdi-calendar-check me-2"></i>
                        Mark VAT Return as Filable
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {selectedVatReturnForFillable && (
                        <>
                            <div className="mb-4 p-3 bg-light rounded">
                                <h6 className="mb-2">VAT Return Period</h6>
                                <p className="mb-1">
                                    <strong>From:</strong> {formatDate(selectedVatReturnForFillable.period_start)}
                                </p>
                                <p className="mb-1">
                                    <strong>To:</strong> {formatDate(selectedVatReturnForFillable.period_end)}
                                </p>
                                <p className="mb-0">
                                    <strong>Net VAT Payable:</strong> AED {formatCurrency(selectedVatReturnForFillable.net_vat_payable)}
                                </p>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Date of Filing <span className="text-danger">*</span>
                                </Form.Label>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        views={["year", "month", "day"]}
                                        format="DD-MM-YYYY"
                                        density="compact"
                                        className='form-feild form-control'
                                        value={
                                            fillableDate && dayjs(fillableDate).isValid()
                                                ? dayjs(fillableDate)
                                                : null
                                        }
                                        onChange={(newValue) => setFillableDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                fullWidth: true,
                                                className: "form-feild",
                                            },
                                            popper: {
                                                disablePortal: true,
                                                sx: {
                                                    zIndex: 2000,
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                {/* <Form.Text className="text-muted">
                                    This is the date when the VAT return is submitted to FTA. Journal entries will be created in Webzash with this date.
                                </Form.Text> */}
                            </Form.Group>

                            {/* <div className="alert alert-info">
                                <i className="mdi mdi-information me-2"></i>
                                <strong>Note:</strong> Once you mark this VAT return as fillable, journal entries will be created in Webzash and the status will change to "Submitted".
                            </div> */}
                        </>
                    )}
                </Offcanvas.Body>
                <div className="p-3 border-top d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={() => setShowFillableModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveFillableDate}
                        disabled={!fillableDate}
                    >
                        <i className="mdi mdi-check me-1"></i>
                        Save
                    </Button>
                </div>
            </Offcanvas>
            {/* Details Offcanvas */}
            <Offcanvas
                show={showDetailsOffcanvas}
                onHide={() => setShowDetailsOffcanvas(false)}
                placement="end"
                style={{ width: '1050px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <Offcanvas.Title className="d-flex align-items-center gap-2 text-white">
                        <FiFileText size={20} />
                        {selectedDetails?.title || 'Details'}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="bg-light">
                    {selectedDetails && (
                        <>
                            {/* Filters and Summary Card */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Form.Group className="d-flex align-items-center gap-2">
                                    <Form.Label className="mb-0 text-muted small text-uppercase fw-semibold">Filter:</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        style={{ width: '150px', fontSize: '0.8rem' }}
                                        value={taxableFilter}
                                        onChange={(e) => setTaxableFilter(e.target.value)}
                                    >
                                        <option value="all">All Entries</option>
                                        <option value="taxable">Standard Taxable</option>
                                        <option value="rcm">RCM Taxable</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h6 className="text-muted mb-1 text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                                Transaction Period
                                            </h6>
                                            <p className="mb-0 fw-medium">
                                                {formatDate(selectedDetails.period_start)} - {formatDate(selectedDetails.period_end)}
                                            </p>
                                        </div>
                                        {selectedReturn && !['submitted', 'filed', 'Submitted', 'Filed'].includes(selectedReturn.status) ? (
                                            <Button variant="primary" className="px-3 py-2 d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }} onClick={async () => {
                                                setReportLoading(true);

                                                try {
                                                    const token = JSON.parse(localStorage.getItem('user-info'))?.token;
                                                    const origin = localStorage.getItem('user-origin');

                                                    let includeUnclaimed = false;
                                                    try {
                                                        const reportDataObj = typeof selectedReturn.report_data === 'string'
                                                            ? JSON.parse(selectedReturn.report_data)
                                                            : (selectedReturn.report_data || selectedReturn.journal_data || {});
                                                        includeUnclaimed = reportDataObj.include_unclaimed === true;
                                                    } catch (e) { }

                                                    const generateResponse = await axios.post(
                                                        `${API_URL}/generate-report`,
                                                        {
                                                            period_start: selectedReturn.period_start,
                                                            period_end: selectedReturn.period_end,
                                                            report_type: 'detailed',
                                                            include_unclaimed: includeUnclaimed
                                                        },
                                                        { headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin } }
                                                    );

                                                    const addResponse = generateResponse.data;
                                                    const finalData = transformReportData(addResponse);

                                                    const saveResponse = await axios.post(
                                                        `${API_URL}/save`,
                                                        {
                                                            id: selectedReturn.id,
                                                            period_start: selectedReturn.period_start,
                                                            period_end: selectedReturn.period_end,
                                                            net_vat_payable: finalData.net_vat_due.net_vat_payable,
                                                            report_data: addResponse,
                                                            generated_date: new Date().toISOString()
                                                        },
                                                        { headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin } }
                                                    );

                                                    setVatReturnData(finalData);
                                                    setSelectedReturn(saveResponse.data.data || saveResponse.data);
                                                    fetchVatReturns(); // Refresh the list in the background
                                                    setRefreshTrigger(selectedDetails.type);

                                                    Swal.fire({
                                                        toast: true,
                                                        position: 'top-end',
                                                        icon: 'success',
                                                        title: 'Report successfully updated',
                                                        showConfirmButton: false,
                                                        timer: 2000
                                                    });
                                                } catch (error) {
                                                    console.error('Error refreshing report:', error);
                                                    Swal.fire('Error', 'Failed to refresh report', 'error');
                                                } finally {
                                                    setReportLoading(false);
                                                }
                                            }}>
                                                <FiRefreshCw size={14} /> Refresh Report
                                            </Button>
                                        ) : (
                                            <Badge bg="primary" className="px-3 py-2">
                                                <FiFileText className="me-1" size={14} />
                                                {selectedDetails.data.length} Entries
                                            </Badge>
                                        )}
                                    </div>

                                    <div className="row g-3 mt-2">
                                        <div className="col-md-3 col-6">
                                            <div className="p-3 rounded h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <div className="bg-primary bg-opacity-10 p-2 rounded">
                                                        <FiDollarSign size={16} className="text-primary" />
                                                    </div>
                                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Taxable</small>
                                                </div>
                                                <h5 className="mb-0 fw-bold text-primary">
                                                    AED {formatCurrency(
                                                        selectedDetails.data
                                                            .filter(e => {
                                                                if (auditFilter === 'checked' && !auditedItems[e.entry_id]) return false;
                                                                if (auditFilter === 'unchecked' && auditedItems[e.entry_id]) return false;
                                                                if (e.claimed === false) return false;
                                                                const isStandardTaxable = (e.vat_amount || 0) > 0 && e.vat_type !== 'reverse_charge';
                                                                const isRcm = e.vat_type === 'reverse_charge';
                                                                if (taxableFilter === 'taxable' && !isStandardTaxable) return false;
                                                                if (taxableFilter === 'rcm' && !isRcm) return false;

                                                                if (detailsMonthFilter) {
                                                                    const d = new Date(e.date);
                                                                    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                                    if (monthStr !== detailsMonthFilter) return false;
                                                                }
                                                                if (detailsVendorFilter) {
                                                                    const vendor = e.client_name || e.ledger_name || e.vendor_name || '-';
                                                                    if (vendor !== detailsVendorFilter) return false;
                                                                }

                                                                return true;
                                                            })
                                                            .reduce((sum, e) => sum + (parseFloat(e.taxable_amount) || 0), 0)
                                                    )}
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="p-3 rounded h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <div className="bg-info bg-opacity-10 p-2 rounded">
                                                        <FiLink size={16} className="text-info" />
                                                    </div>
                                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Adjustment</small>
                                                </div>
                                                <h5 className="mb-0 fw-bold text-info">
                                                    AED {formatCurrency(
                                                        selectedDetails.data
                                                            .filter(e => {
                                                                if (auditFilter === 'checked' && !auditedItems[e.entry_id]) return false;
                                                                if (auditFilter === 'unchecked' && auditedItems[e.entry_id]) return false;
                                                                if (e.claimed === false) return false;
                                                                const isStandardTaxable = (e.vat_amount || 0) > 0 && e.vat_type !== 'reverse_charge';
                                                                const isRcm = e.vat_type === 'reverse_charge';
                                                                if (taxableFilter === 'taxable' && !isStandardTaxable) return false;
                                                                if (taxableFilter === 'rcm' && !isRcm) return false;

                                                                if (detailsMonthFilter) {
                                                                    const d = new Date(e.date);
                                                                    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                                    if (monthStr !== detailsMonthFilter) return false;
                                                                }
                                                                if (detailsVendorFilter) {
                                                                    const vendor = e.client_name || e.ledger_name || e.vendor_name || '-';
                                                                    if (vendor !== detailsVendorFilter) return false;
                                                                }

                                                                return true;
                                                            })
                                                            .reduce((sum, e) => sum + (parseFloat(e.adjustment_amount) || 0), 0)
                                                    )}
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="p-3 rounded h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <div className="bg-success bg-opacity-10 p-2 rounded">
                                                        <FiDollarSign size={16} className="text-success" />
                                                    </div>
                                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>VAT Amount</small>
                                                </div>
                                                <h5 className="mb-0 fw-bold text-success">
                                                    AED {formatCurrency(
                                                        selectedDetails.data
                                                            .filter(e => {
                                                                if (auditFilter === 'checked' && !auditedItems[e.entry_id]) return false;
                                                                if (auditFilter === 'unchecked' && auditedItems[e.entry_id]) return false;
                                                                if (e.claimed === false) return false;
                                                                const isStandardTaxable = (e.vat_amount || 0) > 0 && e.vat_type !== 'reverse_charge';
                                                                const isRcm = e.vat_type === 'reverse_charge';
                                                                if (taxableFilter === 'taxable' && !isStandardTaxable) return false;
                                                                if (taxableFilter === 'rcm' && !isRcm) return false;

                                                                if (detailsMonthFilter) {
                                                                    const d = new Date(e.date);
                                                                    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                                    if (monthStr !== detailsMonthFilter) return false;
                                                                }
                                                                if (detailsVendorFilter) {
                                                                    const vendor = e.client_name || e.ledger_name || e.vendor_name || '-';
                                                                    if (vendor !== detailsVendorFilter) return false;
                                                                }

                                                                return true;
                                                            })
                                                            .reduce((sum, e) => sum + (parseFloat(e.vat_amount) || 0), 0)
                                                    )}
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="p-3 rounded h-100" style={{ backgroundColor: '#e9ecef' }}>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <div className="bg-dark bg-opacity-10 p-2 rounded">
                                                        <FiCheckCircle size={16} className="text-dark" />
                                                    </div>
                                                    <small className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Total Amount</small>
                                                </div>
                                                <h5 className="mb-0 fw-bold text-dark">
                                                    AED {formatCurrency(
                                                        selectedDetails.data
                                                            .filter(e => {
                                                                if (auditFilter === 'checked' && !auditedItems[e.entry_id]) return false;
                                                                if (auditFilter === 'unchecked' && auditedItems[e.entry_id]) return false;
                                                                if (e.claimed === false) return false;
                                                                const isStandardTaxable = (e.vat_amount || 0) > 0 && e.vat_type !== 'reverse_charge';
                                                                const isRcm = e.vat_type === 'reverse_charge';
                                                                if (taxableFilter === 'taxable' && !isStandardTaxable) return false;
                                                                if (taxableFilter === 'rcm' && !isRcm) return false;

                                                                if (detailsMonthFilter) {
                                                                    const d = new Date(e.date);
                                                                    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                                    if (monthStr !== detailsMonthFilter) return false;
                                                                }
                                                                if (detailsVendorFilter) {
                                                                    const vendor = e.client_name || e.ledger_name || e.vendor_name || '-';
                                                                    if (vendor !== detailsVendorFilter) return false;
                                                                }

                                                                return true;
                                                            })
                                                            .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
                                                    )}
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {detailsLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-0">
                                        <div>
                                            <Table hover className="mb-0">
                                                <thead className="table-sales-gradient">
                                                    <tr>
                                                        <th className="border-0 text-center text-muted fw-semibold" style={{ width: '50px', fontSize: '0.8rem' }}>
                                                            <div className="d-flex align-items-center justify-content-center gap-1">#
                                                                <Dropdown>
                                                                    <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted border-0 shadow-none d-flex align-items-center" style={{ textDecoration: 'none' }}>
                                                                        <FiFilter className={auditFilter ? "text-primary" : ""} size={14} />
                                                                    </Dropdown.Toggle>
                                                                    <Dropdown.Menu style={{ fontSize: '0.8rem' }}>
                                                                        <Dropdown.Item onClick={() => setAuditFilter('')} active={auditFilter === ''}>All</Dropdown.Item>
                                                                        <Dropdown.Item onClick={() => setAuditFilter('checked')} active={auditFilter === 'checked'}>Checked</Dropdown.Item>
                                                                        <Dropdown.Item onClick={() => setAuditFilter('unchecked')} active={auditFilter === 'unchecked'}>Un-Checked</Dropdown.Item>
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            </div>
                                                        </th>
                                                        <th className="border-0 text-muted fw-semibold text-start" style={{ fontSize: '0.8rem' }}>
                                                            <div className="d-flex align-items-center gap-2">
                                                                Invoice Date
                                                                <Dropdown>
                                                                    <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted border-0 shadow-none d-flex align-items-center" style={{ textDecoration: 'none' }}>
                                                                        <FiFilter className={detailsMonthFilter ? "text-primary" : ""} size={14} />
                                                                    </Dropdown.Toggle>
                                                                    <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', fontSize: '0.8rem' }}>
                                                                        <Dropdown.Item onClick={() => setDetailsMonthFilter('')} active={detailsMonthFilter === ''}>All Months</Dropdown.Item>
                                                                        {[...new Set(selectedDetails?.data?.map(e => {
                                                                            const d = new Date(e.date);
                                                                            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                                        }) || [])].sort((a, b) => b.localeCompare(a)).map(month => {
                                                                            const [y, m] = month.split('-');
                                                                            const dateObj = new Date(y, m - 1);
                                                                            return <Dropdown.Item key={month} onClick={() => setDetailsMonthFilter(month)} active={detailsMonthFilter === month}>{dateObj.toLocaleString('default', { month: 'short', year: 'numeric' })}</Dropdown.Item>;
                                                                        })}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            </div>
                                                        </th>
                                                        {(detailsType === 'emirate' || detailsType === 'zero_rated') ? (
                                                            <>
                                                                <th className="border-0 text-muted fw-semibold text-start" style={{ fontSize: '0.8rem' }}>Ref No</th>
                                                                <th className="border-0 text-muted fw-semibold text-start" style={{ fontSize: '0.8rem' }}>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        Client
                                                                        <Dropdown>
                                                                            <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted border-0 shadow-none d-flex align-items-center" style={{ textDecoration: 'none' }}>
                                                                                <FiFilter className={detailsVendorFilter ? "text-primary" : ""} size={14} />
                                                                            </Dropdown.Toggle>
                                                                            <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', fontSize: '0.8rem' }}>
                                                                                <Dropdown.Item onClick={() => setDetailsVendorFilter('')} active={detailsVendorFilter === ''}>All</Dropdown.Item>
                                                                                {[...new Set(selectedDetails?.data?.map(e => e.client_name || e.ledger_name || e.vendor_name || '-').filter(Boolean) || [])].sort().map(vendor => (
                                                                                    <Dropdown.Item key={vendor} onClick={() => setDetailsVendorFilter(vendor)} active={detailsVendorFilter === vendor}>{vendor.substring(0, 15)}{vendor.length > 15 ? '...' : ''}</Dropdown.Item>
                                                                                ))}
                                                                            </Dropdown.Menu>
                                                                        </Dropdown>
                                                                    </div>
                                                                </th>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <th className="border-0 text-muted fw-semibold text-start" style={{ fontSize: '0.8rem' }}>Inv No</th>
                                                                <th className="border-0 text-muted fw-semibold text-start" style={{ fontSize: '0.8rem' }}>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        Vendor
                                                                        <Dropdown>
                                                                            <Dropdown.Toggle variant="link" size="sm" className="p-0 text-muted border-0 shadow-none d-flex align-items-center" style={{ textDecoration: 'none' }}>
                                                                                <FiFilter className={detailsVendorFilter ? "text-primary" : ""} size={14} />
                                                                            </Dropdown.Toggle>
                                                                            <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', fontSize: '0.8rem' }}>
                                                                                <Dropdown.Item onClick={() => setDetailsVendorFilter('')} active={detailsVendorFilter === ''}>All</Dropdown.Item>
                                                                                {[...new Set(selectedDetails?.data?.map(e => e.vendor_name || e.client_name || '-').filter(Boolean) || [])].sort().map(vendor => (
                                                                                    <Dropdown.Item key={vendor} onClick={() => setDetailsVendorFilter(vendor)} active={detailsVendorFilter === vendor}>{vendor.substring(0, 15)}{vendor.length > 15 ? '...' : ''}</Dropdown.Item>
                                                                                ))}
                                                                            </Dropdown.Menu>
                                                                        </Dropdown>
                                                                    </div>
                                                                </th>
                                                            </>
                                                        )}
                                                        <th className="border-0 text-muted fw-semibold text-end" style={{ fontSize: '0.8rem' }}>Amount</th>
                                                        <th className="border-0 text-muted fw-semibold text-end" style={{ fontSize: '0.8rem' }}>VAT</th>

                                                        {detailsType === 'recoverable' && <th className="border-0 text-muted fw-semibold text-end" style={{ fontSize: '0.8rem' }}>Adjustment</th>}
                                                        <th className="border-0 text-muted fw-semibold text-end" style={{ fontSize: '0.8rem' }}>Total</th>
                                                        <th className="border-0 text-muted fw-semibold text-center" style={{ fontSize: '0.8rem' }}>Claim</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedDetails.data
                                                        .filter(e => {
                                                            if (auditFilter === 'checked' && !auditedItems[e.entry_id]) return false;
                                                            if (auditFilter === 'unchecked' && auditedItems[e.entry_id]) return false;
                                                            const isStandardTaxable =
                                                                (e.vat_amount || 0) > 0 && e.vat_type !== 'reverse_charge';
                                                            const isRcm = e.vat_type === 'reverse_charge';

                                                            if (taxableFilter === 'taxable' && !isStandardTaxable) return false;
                                                            if (taxableFilter === 'rcm' && !isRcm) return false;

                                                            if (detailsMonthFilter) {
                                                                const d = new Date(e.date);
                                                                const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                                if (monthStr !== detailsMonthFilter) return false;
                                                            }
                                                            if (detailsVendorFilter) {
                                                                const vendor = e.client_name || e.ledger_name || e.vendor_name || '-';
                                                                if (vendor !== detailsVendorFilter) return false;
                                                            }

                                                            return true;
                                                        })
                                                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                                                        .map((entry, index) => (
                                                            <tr key={entry.entry_id || index} className={`align-middle ${auditedItems[entry.entry_id] ? 'audited-row' : ''}`} style={{ transition: 'all 0.2s' }}>
                                                                <td className="py-3 text-center">
                                                                    <div
                                                                        onClick={() => toggleAudit(entry.entry_id)}
                                                                        style={{ cursor: 'pointer', display: 'inline-block' }}
                                                                    >
                                                                        {auditedItems[entry.entry_id] ? (
                                                                            <FiCheckCircle size={20} className="text-success" />
                                                                        ) : (
                                                                            <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #dee2e6' }}></div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 text-start">
                                                                    <small className="text-muted">{formatDate(entry.date)}</small>
                                                                </td>

                                                                <td className="py-3 text-start">
                                                                    <small
                                                                        className="fw-medium text-dark"
                                                                        style={{ cursor: 'pointer', textDecoration: 'none' }}
                                                                        onClick={() => {
                                                                            if (detailsType === 'emirate' || detailsType === 'zero_rated') {
                                                                                handleInvoiceClick(entry);
                                                                            } else {
                                                                                handleExpenseDocClick(entry);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {(detailsType === 'emirate' || detailsType === 'zero_rated')
                                                                            ? (entry.ref_no || entry.reference_no || entry.invoice_no || entry.serial_no || '-')
                                                                            : (entry.invoice_no || entry.ref_no || entry.reference_no || '-')}
                                                                    </small>
                                                                </td>

                                                                <td className="py-3 text-start">
                                                                    <div className="text-truncate fw-medium" style={{ maxWidth: '150px' }}>
    {(entry.is_vat_payment || entry.vat_type === 'vat_payment')
        ? (entry.vendor_name || 'VAT Payable')
        : (detailsType === 'emirate' || detailsType === 'zero_rated')
            ? (entry.client_name || entry.ledger_name || 'N/A')
            : (
                entry.client_name ||
                (['FTA / Government', 'Expense'].includes(entry.vendor_name)
                    ? (entry.ledger_name || entry.vendor_name)
                    : entry.vendor_name
                ) ||
                entry.credit_ledger ||
                'N/A'
            )
    }
</div>

                                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                                        {entry.is_carried_forward && (
                                                                            <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem', width: 'fit-content' }}>
                                                                                Carried from {entry.previous_period ? formatPreviousPeriod(entry.previous_period) : 'prev'}
                                                                            </Badge>
                                                                        )}

                                                                        {entry.bank_charge > 0 && !entry.is_vat_payment && (
                                                                            <Badge bg="secondary" className="bg-opacity-10 text-secondary border-secondary border" style={{ fontSize: '0.6rem', width: 'fit-content' }}>
                                                                                Incl. Charge: AED {formatCurrency(entry.bank_charge)}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                <td className="py-3 text-end">
                                                                    <span className="fw-semibold">{formatCurrency(entry.taxable_amount || 0)}</span>
                                                                </td>
                                                                <td className="py-3 text-end">
                                                                    <span className="fw-semibold text-success">{formatCurrency(entry.vat_amount || 0)}</span>
                                                                </td>
                                                                {detailsType === 'recoverable' && (
                                                                    <td className="py-3 text-end">
                                                                        <span className="text-dark">{formatCurrency(entry.adjustment_amount || 0)}</span>
                                                                    </td>
                                                                )}

                                                                <td className="py-3 text-end">
                                                                    <span className="fw-bold">{formatCurrency(entry.amount || 0)}</span>
                                                                </td>
                                                                <td className="py-3 text-center">
                                                                    <Button
                                                                        variant={entry.claimed ? "success" : "outline-secondary"}
                                                                        size="sm"
                                                                        onClick={() =>
                                                                            toggleClaimStatus(
                                                                                entry.entry_id,
                                                                                entry.claimed || false,
                                                                                detailsType,
                                                                                entry.ledger_id
                                                                            )
                                                                        }
                                                                        className="d-inline-flex align-items-center gap-1 px-2"
                                                                    >
                                                                        {entry.claimed ? (
                                                                            <>
                                                                                <FiCheckCircle size={14} />
                                                                                <span>Claimed</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FiXCircle size={14} />
                                                                                <span>Unclaimed</span>
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}

                            {!detailsLoading && selectedDetails.data.length === 0 && (
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="text-center py-5">
                                        <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                                            <FiFileText size={48} className="text-muted" />
                                        </div>
                                        <h5 className="mb-2">No transactions found</h5>
                                        <p className="text-muted mb-0">No transactions available for this period and category</p>
                                    </Card.Body>
                                </Card>
                            )}
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {/* Invoice Preview Modal */}
            {showPreviewModal && selectedInvoice && (
                <TaxInvoicePreview
                    showModal={showPreviewModal}
                    handleClose={() => {
                        setShowPreviewModal(false);
                        setSelectedQuotation(null);
                    }}
                    createdInvoice={selectedInvoice}
                    quotationData={selectedQuotation}
                    customIntegration={customIntegration}
                    setCustomIntegration={setCustomIntegration}
                    editableDeploymentIntegration={editableDeploymentIntegration}
                    setEditableDeploymentIntegration={setEditableDeploymentIntegration}
                    customIntegrationDescription={customIntegrationDescription}
                    setCustomIntegrationDescription={setCustomIntegrationDescription}
                    onDownloadPDF={handleDownloadTaxInvoicePDF}
                />
            )}

            {/* Document Preview Offcanvas */}
            <DocPreviewOffcanvas
                show={showDocOffcanvas}
                onHide={() => {
                    setShowDocOffcanvas(false);
                    // Only re-open the list if we were actually viewing details
                    if (selectedDetails) {
                        setShowDetailsOffcanvas(true);
                    }
                }}
                url={docUrl}
                downloadUrl={downloadUrl}
                title={docTitle}
            />

            {/* Unclaimed Items Offcanvas */}
            <Offcanvas
                show={showUnclaimedOffcanvas}
                onHide={() => setShowUnclaimedOffcanvas(false)}
                placement="end"
                style={{ width: '1050px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
                    <Offcanvas.Title className="d-flex align-items-center gap-2 font-size-18">
                        <FiAlertCircle className="text-warning" />
                        Unclaimed Items
                        {isFetchingLateItems && <div className="spinner-border spinner-border-sm text-primary ms-2" role="status"></div>}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="bg-light">
                    <div className="mb-4">
                        <p className="text-muted small mb-3">
                            The following items were left unclaimed in previous VAT returns.
                            These items are currently being carried forward.
                        </p>

                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                            <span className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '10px' }}>1</span>
                            Unclaimed Sales ({unclaimedItems?.sales_entries?.length || 0})
                        </h6>
                        <div className="table-responsive bg-white rounded shadow-sm mb-4">
                            <Table hover className="mb-0 small">
                                <thead className="table-sales-gradient">
                                    <tr>
                                        <th>Invoice Date</th>
                                        <th>Ref No</th>
                                        <th>Client/Vendor</th>
                                        <th>Previous Period</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-end">Amount</th>
                                        <th className="text-end">VAT</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unclaimedItems?.sales_entries?.length > 0 ? (
                                        unclaimedItems.sales_entries.map((entry, idx) => (
                                            <tr key={`sales-${idx}`} className="align-middle">
                                                <td>{formatDate(entry.date)}</td>
                                                <td>{entry.ref_no || entry.invoice_no || '-'}</td>
                                                <td>{entry.client_name || entry.vendor_name || entry.ledger_name || '-'}</td>
                                                <td><small className="text-muted">{entry.is_late_added ? <Badge bg="info">Late Added</Badge> : formatPreviousPeriod(entry.previous_period)}</small></td>
                                                <td className="text-center"><Badge bg="secondary">Unclaimed</Badge></td>
                                                <td className="text-end">{formatCurrency(entry.amount)}</td>
                                                <td className="text-end fw-bold text-success">{formatCurrency(entry.vat_amount)}</td>
                                                <td className="text-center">
                                                    <Button variant="outline-danger" size="sm" onClick={() => toggleClaimStatus(entry.entry_id, false, 'emirate', entry.ledger_id)}>
                                                        Unclaimed
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="8" className="text-center py-3 text-muted">No unclaimed sales</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                            <span className="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '10px' }}>2</span>
                            Unclaimed Expenses ({unclaimedItems?.expense_entries?.length || 0})
                        </h6>
                        <div className="table-responsive bg-white rounded shadow-sm mb-4">
                            <Table hover className="mb-0 small">
                                <thead className="table-sales-gradient">
                                    <tr>
                                        <th>Invoice Date</th>
                                        <th>Inv No</th>
                                        <th>Client/Vendor</th>
                                        <th>Previous Period</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-end">Amount</th>
                                        <th className="text-end">VAT</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unclaimedItems?.expense_entries?.length > 0 ? (
                                        unclaimedItems.expense_entries.map((entry, idx) => (
                                            <tr key={`expense-${idx}`} className="align-middle">
                                                <td>{formatDate(entry.date)}</td>
                                                <td>{entry.invoice_no || entry.ref_no || '-'}</td>
                                                <td>{entry.client_name || (entry.vendor_name === 'FTA / Government' ? (entry.ledger_name || entry.vendor_name) : entry.vendor_name) || entry.credit_ledger || entry.ledger_name || '-'}</td>
                                                <td><small className="text-muted">{entry.is_late_added ? <Badge bg="info">Late Added</Badge> : formatPreviousPeriod(entry.previous_period)}</small></td>
                                                <td className="text-center"><Badge bg="secondary">Unclaimed</Badge></td>
                                                <td className="text-end">{formatCurrency(entry.amount)}</td>
                                                <td className="text-end fw-bold text-success">{formatCurrency(entry.vat_amount)}</td>
                                                <td className="text-center">
                                                    <Button variant="outline-danger" size="sm" onClick={() => toggleClaimStatus(entry.entry_id, false, 'recoverable', entry.ledger_id)}>
                                                        Unclaimed
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6" className="text-center py-3 text-muted">No unclaimed expenses</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {unclaimedItems?.vat_payment_entries?.length > 0 && (
                            <>
                                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                    <span className="bg-info bg-opacity-10 text-info rounded-circle d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '10px' }}>3</span>
                                    Unclaimed VAT Payments / Box 9-10 ({unclaimedItems.vat_payment_entries.length})
                                </h6>
                                <div className="table-responsive bg-white rounded shadow-sm">
                                    <Table hover className="mb-0 small">
                                        <thead className="table-sales-gradient">
                                            <tr>
                                                <th>Invoice Date</th>
                                                <th>Narration</th>
                                                <th>Client/Vendor</th>
                                                <th>Previous Period</th>
                                                <th className="text-center">Status</th>
                                                <th className="text-end">Amount</th>
                                                <th className="text-end">VAT</th>
                                                {/* <th className="text-center">Action</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {unclaimedItems.vat_payment_entries.map((entry, idx) => (
                                                <tr key={`vatp-${idx}`} className="align-middle">
                                                    <td>{formatDate(entry.date)}</td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '200px' }}>{entry.narration}</div>
                                                        <div className="d-flex flex-wrap gap-1 mt-1">
                                                            <Badge bg="info" className="fw-normal" style={{ fontSize: '0.65rem', color: 'white' }}>Box 9/10 Settlement</Badge>
                                                        </div>
                                                    </td>
                                                    <td>VAT Payable</td>
                                                    <td><small className="text-muted">{entry.is_late_added ? <Badge bg="info">Late Added</Badge> : formatPreviousPeriod(entry.previous_period)}</small></td>
                                                    <td className="text-center"><Badge bg="secondary">Unclaimed</Badge></td>
                                                    <td className="text-end">{formatCurrency(entry.bank_charge || entry.bank_charges_amount || 0)}</td>
                                                    <td className="text-end fw-bold text-success">{formatCurrency(entry.vat_amount)}</td>
                                                    <td className="text-center">
                                                        <Button variant="outline-danger" size="sm" onClick={() => toggleClaimStatus(`vat_payment_${entry.entry_id}`, false, 'recoverable', entry.ledger_id)}>
                                                            Unclaimed
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </div>
                </Offcanvas.Body>
                <div className="p-3 border-top bg-light text-end">
                    <Button variant="secondary" onClick={() => setShowUnclaimedOffcanvas(false)}>
                        Close
                    </Button>
                </div>
            </Offcanvas>

            {/* Variation Offcanvas */}
            <Offcanvas
                show={showVariationOffcanvas}
                onHide={() => setShowVariationOffcanvas(false)}
                placement="end"
                style={{ width: '500px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom bg-info bg-opacity-10">
                    <Offcanvas.Title className="font-size-18 d-flex align-items-center gap-2">
                        <FiEdit className="text-info" />
                        Manage VAT Variation
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="bg-light p-4">
                    {variationItem && (
                        <div className="mb-4">
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body className="p-4">
                                    <h6 className="fw-bold mb-1">VAT Return Details</h6>
                                    <p className="text-muted small mb-3 border-bottom pb-2">
                                        Period: {formatDate(variationItem.period_start)} to {formatDate(variationItem.period_end)}
                                    </p>

                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Total Output VAT:</span>
                                        <span className="fw-medium">AED {formatCurrency(variationItem.totalOutputVat)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Total Input VAT:</span>
                                        <span className="fw-medium text-success">AED {formatCurrency(variationItem.totalInputVat)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 mt-3 pt-2 border-top">
                                        <span className="text-muted fw-bold">Original VAT Payable:</span>
                                        <span className="fw-bold text-dark fs-5">AED {formatCurrency(variationItem.net_vat_payable)}</span>
                                    </div>
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold">New Total Output VAT Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            className="form-control form-feild"
                                            placeholder={`Enter new amount (Original: ${formatCurrency(variationItem.totalOutputVat)})`}
                                            value={variationForm.output_amount}
                                            onChange={(e) => setVariationForm({ ...variationForm, output_amount: e.target.value })}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold">New Total Input VAT Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            className="form-control form-feild"
                                            placeholder={`Enter new amount (Original: ${formatCurrency(variationItem.totalInputVat)})`}
                                            value={variationForm.input_amount}
                                            onChange={(e) => setVariationForm({ ...variationForm, input_amount: e.target.value })}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">Notes</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            className="form-control form-feild"
                                            placeholder="Enter notes explaining this variation"
                                            value={variationForm.notes}
                                            onChange={(e) => setVariationForm({ ...variationForm, notes: e.target.value })}
                                        />
                                    </Form.Group>

                                    <div className="mt-4 p-3 bg-light bg-opacity-10 rounded text-center border border-primary border-opacity-25">
                                        <span className="text-dark fw-bold d-block mb-1">New Total VAT Payable</span>
                                        <h3 className="text-dark fw-bold mb-0">
                                            AED {formatCurrency(
                                                (variationForm.output_amount !== '' && variationForm.output_amount !== null ? parseFloat(variationForm.output_amount) : parseFloat(variationItem.totalOutputVat || 0))
                                                - (variationForm.input_amount !== '' && variationForm.input_amount !== null ? parseFloat(variationForm.input_amount) : parseFloat(variationItem.totalInputVat || 0))
                                            )}
                                        </h3>
                                    </div>

                                    <Button variant="primary" className="w-100 mt-4 py-2 fw-bold" onClick={handleSaveVariation}>
                                        Save Variation
                                    </Button>
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {/* Variation Details Offcanvas */}
            <Offcanvas
                show={showVariationDetailsOffcanvas}
                onHide={() => setShowVariationDetailsOffcanvas(false)}
                placement="end"
                style={{ width: '550px' }}
            >
                <Offcanvas.Header closeButton className="border-bottom bg-info bg-opacity-10">
                    <Offcanvas.Title className="font-size-18 d-flex align-items-center gap-2">
                        <FiEdit className="text-info" />
                        Variation Details
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="bg-light p-4">
                    {selectedVariationDetails && (() => {
                        const reportData = typeof selectedVariationDetails.report_data === 'string'
                            ? JSON.parse(selectedVariationDetails.report_data)
                            : (selectedVariationDetails.report_data || selectedVariationDetails.journal_data || {});

                        const origOutput = parseFloat(reportData?.net_vat_due?.total_due_tax || reportData?.sales_data?.total_vat || 0);
                        const origInput = parseFloat(reportData?.net_vat_due?.total_recoverable_tax || reportData?.expenses_data?.total_vat || 0);

                        const outVar = parseFloat(selectedVariationDetails.output_variation_amount || 0);
                        const inVar = parseFloat(selectedVariationDetails.input_variation_amount || 0);

                        const newOutput = origOutput + outVar;
                        const newInput = origInput + inVar;
                        const curPayable = newOutput - newInput;

                        return (
                            <div className="mb-4">
                                {/* Notes Section First */}
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Body className="p-3">
                                        <h6 className="fw-bold mb-2 text-muted">Notes</h6>
                                        <p className="mb-0" style={{ fontSize: '0.95rem' }}>{selectedVariationDetails.variation_notes || 'N/A'}</p>
                                    </Card.Body>
                                </Card>

                                {/* Variation Breakdown */}
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4">
                                        <h6 className="fw-bold mb-1">VAT Return Details</h6>
                                        <p className="text-muted small mb-3 border-bottom pb-2">
                                            Period: {formatDate(selectedVariationDetails.period_start)} to {formatDate(selectedVariationDetails.period_end)}
                                        </p>

                                        <div className="d-flex justify-content-between mb-2 pt-2">
                                            <span className="text-muted">Original Due VAT (Output):</span>
                                            <span className="fw-medium text-dark">AED {formatCurrency(origOutput)}</span>
                                        </div>
                                        {outVar !== 0 && (
                                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom text-info">
                                                <span>New Due VAT (Output):</span>
                                                <span className="fw-bold">AED {formatCurrency(newOutput)}</span>
                                            </div>
                                        )}
                                        {outVar === 0 && <div className="border-bottom mb-3 pb-2"></div>}

                                        <div className="d-flex justify-content-between mb-2 pt-2">
                                            <span className="text-muted">Original Recoverable VAT (Input):</span>
                                            <span className="fw-medium text-dark">AED {formatCurrency(origInput)}</span>
                                        </div>
                                        {inVar !== 0 && (
                                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom text-info">
                                                <span>New Recoverable VAT (Input):</span>
                                                <span className="fw-bold">AED {formatCurrency(newInput)}</span>
                                            </div>
                                        )}
                                        {inVar === 0 && <div className="border-bottom mb-3 pb-2"></div>}

                                        <div className="d-flex justify-content-between mt-3 pt-2">
                                            <span className="text-primary fw-bold">Final VAT Payable:</span>
                                            <span className="fw-bold text-primary fs-4">AED {formatCurrency(curPayable)}</span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        );
                    })()}
                </Offcanvas.Body>
            </Offcanvas>

        </div>
    );
};

const DocPreviewOffcanvas = ({ show, onHide, url, downloadUrl, title }) => (
    <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '1050px' }}>
        <Offcanvas.Header closeButton className="border-bottom bg-gradient py-2" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                <Offcanvas.Title className="fw-bold fs-6 mb-0 text-white d-flex align-items-center gap-2">
                    <FiFileText size={18} />
                    <span className="text-truncate" style={{ maxWidth: '400px' }}>{title}</span>
                </Offcanvas.Title>
                <Button
                    variant="light"
                    size="sm"
                    className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-3 fw-medium"
                    style={{ fontSize: '0.8rem' }}
                    onClick={async () => {
                        try {
                            const response = await axios.get(downloadUrl, {
                                responseType: 'blob',
                                headers: {
                                    Authorization: `Bearer ${JSON.parse(localStorage.getItem('user-info'))?.token}`,
                                    'Tenant-Origin': localStorage.getItem('user-origin')
                                }
                            });
                            const fileName = title.replace(/[:\\/*?"<>|]/g, '_') + (url.endsWith('.pdf') ? '.pdf' : '');
                            saveAs(response.data, fileName);
                        } catch (error) {
                            console.error("Download error:", error);
                            window.open(url, '_blank');
                        }
                    }}
                >
                    <FiDownload size={14} className="text-primary" />
                    <span className="text-primary">Download</span>
                </Button>
            </div>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
            {url ? (
                <iframe
                    src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
                    title={title}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    frameBorder="0"
                />
            ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status"></div>
                        <p className="text-muted">Loading document...</p>
                    </div>
                </div>
            )}
        </Offcanvas.Body>
    </Offcanvas>
);

export default VatReturnReport;



